/**
 * tenderApi.ts — TanStack Query hooks for the tender domain backend
 * (tender-api, port 8000 — live). This is the Ingest/Projects surface's data
 * layer (Track A): it replaces the mock projects table in IngestPage with real,
 * RLS-scoped data from the live service.
 *
 * Built on the existing `api.cmeinsight` client (api.ts), which is already
 * pre-bound to the tender audience (config.auth0Audience). Mirrors the proven
 * kbApi.ts pattern: wire DTOs (api/schemas.py) → frontend models, absorbing the
 * convention mismatches at this seam so the UI never sees them:
 *   - status is a snake_case enum on the wire ("audit"); the UI shows an Italian
 *     display label ("Audit in corso") via STATUS_LABEL.
 *   - timestamps arrive as ISO datetimes; the UI wants a relative label
 *     ("2 ore fa") computed by `relativeTime`.
 *   - the CME total (total_value) is null until an analysis has produced line
 *     items — the UI renders "—" in that case.
 *   - goal 55B, L-1: every money total is the sum of the amounts READ, beside a count of the
 *     amounts nobody read (`unread_amounts`, 0 on an older server). A total that is null
 *     WITH unread amounts is «non determinabile» — never «—», never € 0.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { ApiError, useApi, type Api } from "./api";

// ── Projects ─────────────────────────────────────────────────────────────────

/** Goal 55B, L-1: the one wording of a count of unread amounts. */
export function unreadAmountsWords(n: number): string {
  return n === 1 ? "1 importo non letto" : `${n} importi non letti`;
}

/** Backend status enum (api/models.py ProjectStatus). */
export type TenderProjectStatus = "ingest" | "audit" | "strategy" | "completed" | "archived";

/** Italian display label per backend status, for the dashboard pills. */
export const STATUS_LABEL: Record<TenderProjectStatus, string> = {
  ingest: "Ingest",
  audit: "Audit in corso",
  strategy: "Strategia",
  completed: "Completato",
  archived: "Archiviato",
};

/** Wire shape of ProjectSummary (api/schemas.py). */
interface ProjectSummaryDto {
  id: string;
  name: string;
  city: string | null;
  status: string;
  surface_m2: number | null;
  created_by_sub: string;
  created_at: string;
  updated_at: string;
  document_count: number;
  /** Total value of the CME (€); null until an analysis has run. */
  total_value: number | null;
  /** Goal 55B, L-1: amounts nobody read (absent on an older server → 0). */
  unread_amounts?: number;
}

interface ProjectListDto {
  projects: ProjectSummaryDto[];
  total: number;
  limit: number;
  offset: number;
}

export interface TenderProject {
  id: string;
  name: string;
  city: string | null;
  status: TenderProjectStatus;
  /** Italian display label for `status`. */
  statusLabel: string;
  surfaceM2: number | null;
  /** Total value of the CME in €, or null if the project hasn't been analyzed. Goal 55B, L-1:
   * the sum of the amounts READ — also null when every amount is unread (`unreadAmounts` > 0). */
  totalValue: number | null;
  /** Amounts nobody read behind `totalValue` (absent → none). */
  unreadAmounts?: number;
  documentCount: number;
  createdBySub: string;
  createdAt: string;
  updatedAt: string;
  /** Relative "updated" label ("2 ore fa", "ieri", "3 giorni fa"). */
  updatedLabel: string;
}

const KNOWN_STATUS = new Set<TenderProjectStatus>([
  "ingest",
  "audit",
  "strategy",
  "completed",
  "archived",
]);

function resolveStatus(raw: string): TenderProjectStatus {
  return KNOWN_STATUS.has(raw as TenderProjectStatus) ? (raw as TenderProjectStatus) : "ingest";
}

/**
 * Relative-time label in Italian, matching the prototype's tone ("2 ore fa",
 * "ieri", "3 giorni fa", "1 settimana fa"). `now` is injectable for tests.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((now - then) / 1000));
  const mins = Math.floor(secs / 60);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  if (secs < 60) return "ora";
  if (mins < 60) return mins === 1 ? "1 minuto fa" : `${mins} minuti fa`;
  if (hours < 24) return hours === 1 ? "1 ora fa" : `${hours} ore fa`;
  if (days === 1) return "ieri";
  if (days < 7) return `${days} giorni fa`;
  if (weeks < 5) return weeks === 1 ? "1 settimana fa" : `${weeks} settimane fa`;
  return new Date(iso).toISOString().slice(0, 10);
}

export function toTenderProject(dto: ProjectSummaryDto, now?: number): TenderProject {
  const status = resolveStatus(dto.status);
  return {
    id: dto.id,
    name: dto.name,
    city: dto.city,
    status,
    statusLabel: STATUS_LABEL[status],
    surfaceM2: dto.surface_m2,
    totalValue: dto.total_value,
    unreadAmounts: dto.unread_amounts ?? 0,
    documentCount: dto.document_count,
    createdBySub: dto.created_by_sub,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    updatedLabel: relativeTime(dto.updated_at, now),
  };
}

export interface ProjectListFilters {
  status?: TenderProjectStatus;
  q?: string;
  includeArchived?: boolean;
}

/** Live projects list for the active tenant (RLS-scoped server-side). */
export function useProjects(filters: ProjectListFilters = {}): UseQueryResult<TenderProject[]> {
  const api = useApi();
  const { status, q, includeArchived } = filters;
  return useQuery<TenderProject[]>({
    queryKey: ["tender", "projects", { status, q, includeArchived }],
    queryFn: async ({ signal }): Promise<TenderProject[]> => {
      const res = await api.cmeinsight.get<ProjectListDto>("/v1/projects", {
        signal,
        params: { status, q, include_archived: includeArchived },
      });
      return res.projects.map((p) => toTenderProject(p));
    },
  });
}

/** Fetch one project. */
export function useProject(projectId: string | null): UseQueryResult<TenderProject> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "project", projectId],
    enabled: !!projectId,
    queryFn: async ({ signal }): Promise<TenderProject> => {
      const dto = await api.cmeinsight.get<ProjectSummaryDto>(`/v1/projects/${projectId}`, {
        signal,
      });
      return toTenderProject(dto);
    },
  });
}

// ── Dashboard stats ──────────────────────────────────────────────────────────

/** Wire shape of ProjectStats (api/schemas.py) — org-wide dashboard aggregates. */
interface ProjectStatsDto {
  active_projects: number;
  analyzed_projects: number;
  total_cme_value: number | null;
  /** Goal 55B, L-1 (absent on an older server → 0). */
  unread_amounts?: number;
  total_documents: number;
  open_discrepancies: number;
  critical_discrepancies: number;
}

export interface DashboardStats {
  activeProjects: number;
  analyzedProjects: number;
  /** Goal 55B, L-1: the sum of the amounts READ; null when every amount is unread. */
  totalCmeValue: number | null;
  unreadAmounts?: number;
  totalDocuments: number;
  openDiscrepancies: number;
  criticalDiscrepancies: number;
}

export function toDashboardStats(dto: ProjectStatsDto): DashboardStats {
  return {
    activeProjects: dto.active_projects,
    analyzedProjects: dto.analyzed_projects,
    totalCmeValue: dto.total_cme_value,
    unreadAmounts: dto.unread_amounts ?? 0,
    totalDocuments: dto.total_documents,
    openDiscrepancies: dto.open_discrepancies,
    criticalDiscrepancies: dto.critical_discrepancies,
  };
}

/** Org-wide aggregates for the workspace dashboard stat cards (GET /v1/projects/stats). */
export function useDashboardStats(): UseQueryResult<DashboardStats> {
  const api = useApi();
  return useQuery<DashboardStats>({
    queryKey: ["tender", "projects", "stats"],
    queryFn: async ({ signal }): Promise<DashboardStats> => {
      const dto = await api.cmeinsight.get<ProjectStatsDto>("/v1/projects/stats", { signal });
      return toDashboardStats(dto);
    },
  });
}

export interface CreateProjectInput {
  name: string;
  city?: string;
  surfaceM2?: number;
}

/** Create a tender project (POST /v1/projects). Refreshes the list on success. */
export function useCreateProject(): UseMutationResult<TenderProject, Error, CreateProjectInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, city, surfaceM2 }: CreateProjectInput): Promise<TenderProject> => {
      const dto = await api.cmeinsight.post<ProjectSummaryDto>("/v1/projects", {
        body: { name, city, surface_m2: surfaceM2 },
      });
      return toTenderProject(dto);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
    },
  });
}

/** Soft-delete a project (DELETE /v1/projects/{id} → status archived). */
export function useDeleteProject(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<void> => api.cmeinsight.del<void>(`/v1/projects/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
    },
  });
}

// ── Documents ──────────────────────────────────────────────────────────────

/** Which input a document plays in the analyze pipeline (api/models.py DocumentRole). */
export type TenderDocRole = "cme" | "full_price" | "np" | "info" | "regional" | "drawing" | "other";

/** Italian display label per document role, for the upload UI. */
export const DOC_ROLE_LABEL: Record<TenderDocRole, string> = {
  cme: "CME (computo metrico)",
  full_price: "Elenco Prezzi (EP)",
  np: "Nuovi Prezzi (AP/NP)",
  info: "Relazione",
  regional: "Listino regionale (.xlsx)",
  drawing: "Disegno (allegato)",
  other: "Altro",
};

/**
 * Best-effort role guess from a filename, mirroring the backend's _infer_role
 * (api/documents.py) so the upload UI's default role matches what the server
 * would infer. The user can override it per file; the explicit role wins.
 * Short keywords (EP/AP/NP) match only as whole tokens — bare substrings turned
 * "deposito" into full_price and "capitolato" into np.
 */
export function guessRole(filename: string): TenderDocRole {
  const name = filename.toLowerCase();
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
  // `.xlsx` only. A `.xls` guessed as `regional` is now REFUSED by the server
  // (the comparison stage cannot read one — the shipped image declares no
  // `xlrd`), so guessing it here turned an ordinary drag-and-drop of a legacy
  // spreadsheet into a 422 the user did not ask for. Guessing `other` stores
  // the file as an ordinary attachment, which is what it is; a user who really
  // means it as a listino gets the named refusal from the card's own control.
  if (ext === ".xlsx") return "regional";
  // Explicitly, not by falling through. Removing `.xls` from the branch above
  // let it reach the KEYWORD rules instead — measured: `CME_listino.xls`
  // became "cme", `Relazione_prezzi.xls` became "info". A spreadsheet the
  // comparison stage cannot read is an ordinary attachment, and the server's
  // `_infer_role` says the same thing for the same reason.
  if (ext === ".xls" || ext === ".xlsm" || ext === ".csv") return "other";
  if (ext === ".dwg") return "drawing";
  if (name.includes("cme")) return "cme";
  if (name.includes("relazione")) return "info";
  const tokens = new Set(name.split(/[^a-z0-9]+/));
  if (tokens.has("ep")) return "full_price";
  if (tokens.has("ap") || tokens.has("np")) return "np";
  return "other";
}

/**
 * The document roles an analysis cannot run without — mirror of the Python
 * REQUIRED_ROLES in tender-api analysis/extract.py (keep the two in sync).
 * Used to explain what is missing, and to head the "Richiesti dall'analisi"
 * group. NOT used to gate the start: the server's 422 is the only authority on
 * whether a run may begin (`P-31`; the client-side prediction that used to sit
 * here broke three rounds running and was deleted).
 */
export const REQUIRED_ANALYZE_ROLES: readonly TenderDocRole[] = ["cme", "full_price", "np", "info"];

/**
 * Not required, but it unlocks the comparison against a price book. A public
 * portal never ships a listino — it is the buyer's own reference data — so
 * demanding one to start guaranteed a refusal on every genuine tender download.
 */
export const OPTIONAL_ANALYZE_ROLES: readonly TenderDocRole[] = ["regional"];

/**
 * What intake concluded about a document. Flat on the list DTO, nested under
 * `intake` on the upload DTO — the two endpoints genuinely return different
 * shapes, so they get different types rather than one type used twice.
 */
interface DocumentIntakeDto {
  doc_kind: string;
  label_it: string;
  kind_signal: string;
  kind_confidence: number;
  needs_review: boolean;
  /** WHY it is flagged, as the sentence to print. Derived server-side from the
   *  stored reason codes, exactly as `label_it` is derived from `doc_kind` — the
   *  client must not own a second copy of a sentence. `null` on a document
   *  nobody is being asked about, and on one flagged before migration 0017.
   *
   *  `review_reason` (the machine codes) is on the wire too and deliberately not
   *  read here: no screen branches on it, and a field nothing consumes is not a
   *  property of the system. */
  review_reason_it: string | null;
  analysis_use: boolean;
  kb_ingest: boolean;
  kb_depth: string;
  page_count: number | null;
  title_block: string | null;
  folder_path: string | null;
  doc_code: string | null;
  /** The code the bundle's Elenco elaborati uses for this file, present ONLY
   *  when it disagrees with `doc_code`. The screen joins the two rather than
   *  choosing between them. */
  declared_doc_code: string | null;
  doc_revision: string | null;
  signed_by: string | null;
}

interface DocumentSummaryDto {
  id: string;
  project_id: string;
  original_filename: string;
  doc_type: string;
  role: string;
  size_bytes: number;
  status: string;
  uploaded_by_sub: string;
  created_at: string;
  doc_kind: string;
  label_it: string;
  kind_signal: string;
  kind_confidence: number;
  needs_review: boolean;
  /** WHY it is flagged, as the sentence to print. Derived server-side from the
   *  stored reason codes, exactly as `label_it` is derived from `doc_kind` — the
   *  client must not own a second copy of a sentence. `null` on a document
   *  nobody is being asked about, and on one flagged before migration 0017.
   *
   *  `review_reason` (the machine codes) is on the wire too and deliberately not
   *  read here: no screen branches on it, and a field nothing consumes is not a
   *  property of the system. */
  review_reason_it: string | null;
  analysis_use: boolean;
  kb_ingest: boolean;
  kb_depth: string;
  kb_forward_state: string;
  kb_forwarded_at: string | null;
  /** What this document became in the Knowledge Base. `null` until it is
   *  forwarded — most documents, most of the time. */
  kb_document_id: string | null;
  /** Gate B, 2026-09-28 — why this document did NOT go to the Knowledge Base,
   *  in the customer's words, or null. Derived by the server from facts on the
   *  row; the operator's error text stays off the wire. */
  kb_forward_note?: string | null;
  page_count: number | null;
  title_block: string | null;
  folder_path: string | null;
  doc_code: string | null;
  /** See DocumentIntakeDto: present only on a disagreement. */
  declared_doc_code: string | null;
  doc_revision: string | null;
  signed_by: string | null;
}

interface DocumentCreatedDto {
  id: string;
  project_id: string;
  original_filename: string;
  doc_type: string;
  role: string;
  size_bytes: number;
  status: string;
  uploaded_by_sub: string;
  created_at: string;
  /** Top-level, not inside `intake`: it is a lifecycle state, not something
   *  intake concluded — and the backend keeps the two schemas at field parity. */
  kb_forward_state: string;
  kb_forwarded_at: string | null;
  /** What this document became in the Knowledge Base. `null` until it is
   *  forwarded — most documents, most of the time. */
  kb_document_id: string | null;
  /** Parity with `DocumentSummaryDto`; always null on an upload response. */
  kb_forward_note?: string | null;
  /** Absent when the file was too large to inspect, so it was never classified. */
  intake: DocumentIntakeDto | null;
}

interface DocumentListDto {
  documents: DocumentSummaryDto[];
  total: number;
}

export interface ArchiveSkipped {
  name: string;
  reason: string;
  /** True when this is a file the user supplied that did not arrive.
   *  False for the container's own bookkeeping — directory records, `__MACOSX/`,
   *  `._` sidecars. Absent from a server older than `P-45`. */
  lost?: boolean;
}

interface ArchiveImportedDto {
  documents: DocumentCreatedDto[];
  skipped: ArchiveSkipped[];
  total_entries: number;
  /** Absent from a server older than `P-45` — see `ArchiveImported.lostFiles`. */
  lost_files?: number;
}

export interface ArchiveImported {
  documents: TenderDocument[];
  skipped: ArchiveSkipped[];
  /** Every member the ZIP declared, INCLUDING its own directory records and
   *  archiver metadata. Never show this to a human as "of N": the two real
   *  TORINO portal downloads declare 82 and 68 entries for 35 and 30 documents. */
  totalEntries: number;
  /** Files the user supplied that did not arrive, or `null` when the server did
   *  not say.
   *
   *  `null` is not zero, and the distinction is the whole point of sending the
   *  count rather than counting `skipped` here. The two halves of this system
   *  deploy separately, so a client that infers the number from rows lacking
   *  `lost` must guess: guess false and a real partial import goes silent, guess
   *  true and every healthy portal ZIP raises a false alarm about its own
   *  folders. `null` lets the caller say nothing, which is the only honest
   *  answer when it does not know. */
  lostFiles: number | null;
}

export interface TenderDocument {
  id: string;
  projectId: string;
  filename: string;
  docType: string;
  role: TenderDocRole;
  roleLabel: string;
  sizeBytes: number;
  status: string;
  uploadedBySub: string;
  createdAt: string;
  /** What kind of tender document this is, in the Italian taxonomy. */
  docKind: string;
  /** The kind's Italian name — shown to the user; empty if unrecognised. */
  labelIt: string;
  kindSignal: string;
  kindConfidence: number;
  /** The guess was weak enough that a human should confirm it. */
  needsReview: boolean;
  /** WHY, in one short Italian sentence, for the row to print beside the flag.
   *
   *  Replaced the confidence percentage on 2026-08-28. `kindConfidence` is still
   *  carried — the API returns it and this model mirrors the API — but no screen
   *  renders it: 0,48 for a sheet size and 0,45 for a folder name are signal
   *  weights, and printing them as «48%» had been reading as a probability. */
  reviewReasonIt: string | null;
  analysisUse: boolean;
  kbIngest: boolean;
  /** What has actually HAPPENED about the knowledge base, as opposed to what
   *  `kbIngest` says should. `"pending"` is the set an Admin approves. A plain
   *  string, not a union: the column has no CHECK constraint, so one legacy row
   *  must not break the render. */
  kbForwardState: string;
  kbForwardedAt: string | null;
  /** The Knowledge Base's own id for this document, once forwarded.
   *  Surfaced so the Fase-2 percentage can follow WHICH documents are
   *  converted rather than how many — counting alone cannot tell an
   *  approved document apart from an unrelated leftover. */
  kbDocumentId: string | null;
  /** Gate B — the server's sentence for why this document did not go to the
   *  Knowledge Base (too large to send, or the send failed), or null. A
   *  `skipped` row with no note is a person's choice; one WITH a note is not. */
  kbForwardNote: string | null;
  /** `null` means never measured — a document uploaded before KB-11, or one
   *  whose pages yielded no text. Render the absence, never a 0. */
  pageCount: number | null;
  titleBlock: string | null;
  /** Where the file sat inside the uploaded bundle; null at the root. */
  folderPath: string | null;
  docCode: string | null;
  /** The Elenco elaborati's own code for this file, when it disagrees with
   *  `docCode`. Both are kept and both are shown — «A_or_B» — because which one
   *  is correct is not ours to decide. */
  declaredDocCode: string | null;
  docRevision: string | null;
  signedBy: string | null;
}

const KNOWN_ROLE = new Set<TenderDocRole>([
  "cme",
  "full_price",
  "np",
  "info",
  "regional",
  "drawing",
  "other",
]);

export function toTenderDocument(dto: DocumentSummaryDto): TenderDocument {
  const role = KNOWN_ROLE.has(dto.role as TenderDocRole) ? (dto.role as TenderDocRole) : "other";
  return {
    id: dto.id,
    projectId: dto.project_id,
    filename: dto.original_filename,
    docType: dto.doc_type,
    role,
    roleLabel: DOC_ROLE_LABEL[role],
    sizeBytes: dto.size_bytes,
    status: dto.status,
    uploadedBySub: dto.uploaded_by_sub,
    createdAt: dto.created_at,
    docKind: dto.doc_kind ?? "altro",
    labelIt: dto.label_it ?? "",
    kindSignal: dto.kind_signal ?? "fallback",
    kindConfidence: dto.kind_confidence ?? 0,
    needsReview: dto.needs_review ?? false,
    reviewReasonIt: dto.review_reason_it ?? null,
    analysisUse: dto.analysis_use ?? false,
    kbIngest: dto.kb_ingest ?? false,
    kbForwardState: dto.kb_forward_state ?? "not_applicable",
    kbForwardedAt: dto.kb_forwarded_at ?? null,
    kbDocumentId: dto.kb_document_id ?? null,
    kbForwardNote: dto.kb_forward_note ?? null,
    pageCount: dto.page_count ?? null,
    titleBlock: dto.title_block ?? null,
    folderPath: dto.folder_path ?? null,
    docCode: dto.doc_code ?? null,
    declaredDocCode: dto.declared_doc_code ?? null,
    docRevision: dto.doc_revision ?? null,
    signedBy: dto.signed_by ?? null,
  };
}

/**
 * Map the upload response, whose intake block is nested and may be absent.
 *
 * Deliberately separate from `toTenderDocument`: the two endpoints return
 * different shapes, and using one mapper for both is exactly how every upload
 * came back with `uploadedBySub: undefined`.
 */
export function createdToTenderDocument(dto: DocumentCreatedDto): TenderDocument {
  const role = KNOWN_ROLE.has(dto.role as TenderDocRole) ? (dto.role as TenderDocRole) : "other";
  const intake = dto.intake;
  return {
    id: dto.id,
    projectId: dto.project_id,
    filename: dto.original_filename,
    docType: dto.doc_type,
    role,
    roleLabel: DOC_ROLE_LABEL[role],
    sizeBytes: dto.size_bytes,
    status: dto.status,
    uploadedBySub: dto.uploaded_by_sub,
    createdAt: dto.created_at,
    docKind: intake?.doc_kind ?? "altro",
    labelIt: intake?.label_it ?? "",
    kindSignal: intake?.kind_signal ?? "fallback",
    kindConfidence: intake?.kind_confidence ?? 0,
    // A document that was never classified is flagged, not quietly trusted.
    needsReview: intake ? intake.needs_review : true,
    // No client-side fallback sentence when the block is absent. That path is the
    // streamed oversized upload, which never classifies: the row shows «Da
    // confermare» with no reason until the list reloads, and the server-stored
    // reason then supplies it. Hard-coding the Italian here would be a second
    // copy of a sentence in the one file whose comments say not to keep one.
    reviewReasonIt: intake?.review_reason_it ?? null,
    analysisUse: intake?.analysis_use ?? false,
    kbIngest: intake?.kb_ingest ?? false,
    // From the TOP level, not from `intake` — reading it off the nested block
    // would silently yield undefined and report every upload as not_applicable,
    // which is the same bug this mapper's docstring already records once.
    kbForwardState: dto.kb_forward_state ?? "not_applicable",
    kbForwardedAt: dto.kb_forwarded_at ?? null,
    kbDocumentId: dto.kb_document_id ?? null,
    kbForwardNote: dto.kb_forward_note ?? null,
    pageCount: intake?.page_count ?? null,
    titleBlock: intake?.title_block ?? null,
    folderPath: intake?.folder_path ?? null,
    docCode: intake?.doc_code ?? null,
    declaredDocCode: intake?.declared_doc_code ?? null,
    docRevision: intake?.doc_revision ?? null,
    signedBy: intake?.signed_by ?? null,
  };
}

export interface DocumentKindOption {
  value: string;
  labelIt: string;
  role: TenderDocRole;
  analysisUse: boolean;
  kbIngest: boolean;
}

interface DocumentKindListDto {
  kinds: {
    value: string;
    label_it: string;
    role: string;
    analysis_use: boolean;
    kb_ingest: boolean;
  }[];
}

/**
 * The Italian document taxonomy (GET /v1/document-kinds).
 *
 * Fetched rather than hardcoded here: the same taxonomy decides each kind's
 * pipeline role and KB routing on the server, and a second copy in TypeScript
 * would drift from it without anything failing. Cached indefinitely — it
 * changes only when the backend ships.
 */
export function useDocumentKinds(): UseQueryResult<DocumentKindOption[]> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "document-kinds"],
    staleTime: Infinity,
    queryFn: async ({ signal }): Promise<DocumentKindOption[]> => {
      const res = await api.cmeinsight.get<DocumentKindListDto>("/v1/document-kinds", { signal });
      return res.kinds.map((k) => ({
        value: k.value,
        labelIt: k.label_it,
        role: KNOWN_ROLE.has(k.role as TenderDocRole) ? (k.role as TenderDocRole) : "other",
        analysisUse: k.analysis_use,
        kbIngest: k.kb_ingest,
      }));
    },
  });
}

/**
 * States a document can LEAVE on its own, with no further action from anybody.
 *
 * `P-78` — the poll's trigger. Written as the set of states that are still
 * MOVING rather than as "not done", deliberately: `pending` and `skipped` are
 * both terminal until a person acts, and polling them would burn a request
 * every few seconds for a change that cannot arrive without a click. `queued`
 * and `forwarding` are the two the server advances by itself.
 */
const KB_FORWARD_IN_FLIGHT = new Set(["queued", "forwarding"]);

/**
 * Documents attached to a project (GET /v1/projects/{id}/documents).
 *
 * `P-78` — this query declared NO `refetchInterval`, and `App.tsx` sets
 * `refetchOnWindowFocus: false` globally with `staleTime: 5 min`, so the counts
 * it feeds were pinned to page-mount state and could never change. Returning to
 * the tab did not refresh them either.
 *
 * Omar hit it on 12 Sep and reported the «Processo 2 — Knowledge Base» lane as
 * «stuck»: it read «0 inviati · 18 in invio» while every one of the 18 had been
 * `forwarded` since 23:09:19 the previous night, **13 hours earlier**. Nothing
 * was wrong with the work; the panel could not learn anything new. The sibling
 * Knowledge Base query DOES poll, so the PERCENTAGE beside the sentence moved
 * while the sentence could not — one card carrying two numbers of different
 * ages.
 *
 * `staleTime: 0` is not decoration beside the interval. The global 5-minute
 * `staleTime` would let a returning `refetch` serve the cache instead of the
 * network, so the interval alone would have fired and changed nothing for up to
 * five minutes — the same defect, slower.
 */
export function useProjectDocuments(projectId: string | null): UseQueryResult<TenderDocument[]> {
  const api = useApi();
  // Explicitly parameterised, like `useKbProjectDocuments`: with a
  // `refetchInterval` callback present TypeScript stops inferring the result
  // type from `queryFn` and falls back to `unknown`.
  return useQuery<TenderDocument[]>({
    queryKey: ["tender", "project", projectId, "documents"],
    enabled: !!projectId,
    staleTime: 0,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((d) => KB_FORWARD_IN_FLIGHT.has(d.kbForwardState))
        ? 2500
        : false,
    queryFn: async ({ signal }): Promise<TenderDocument[]> => {
      const res = await api.cmeinsight.get<DocumentListDto>(`/v1/projects/${projectId}/documents`, {
        signal,
      });
      return res.documents.map(toTenderDocument);
    },
  });
}

/** Wire shape of GET /v1/projects/{id}/price-lists (`P-44`). */
export interface ProjectPriceListDto {
  edition_id: string;
  publisher?: string;
  region: string | null;
  year: number;
  title: string;
  source_url: string | null;
  auto: boolean;
  matched_on: string | null;
  redistributable?: boolean;
  files: { id?: string | null; filename: string; section: string | null }[];
}

/** A library price-list edition associated with a project (`P-44`). */
export interface ProjectPriceList {
  editionId: string;
  /** WHO published it (`P-58`). Every book has one; only an official regional
   * prezzario has a region, which is why DEI no longer needs a fake one. */
  publisher: string | null;
  region: string | null;
  year: number;
  title: string;
  sourceUrl: string | null;
  /** True = region detection associated it; false = a person chose it. */
  auto: boolean;
  /** The detector's evidence — what it matched, in which document. */
  matchedOn: string | null;
  /** May the CUSTOMER download this book (`P-58`)? A public regional prezzario
   * yes; a licensed commercial one — DEI — no, per Omar 29 Aug: *"the client
   * can see that this source was used, but cannot download the original
   * file."*
   *
   * Defaults TRUE on absence, and that is the safe direction here rather than
   * the reckless one: an older server sends no such field AND its library holds
   * only public regional editions, so `true` describes every book it can be
   * asked about. It never opens a download — the file's `id` and the endpoint
   * are what do that, and both refuse independently. This flag only decides
   * whether the card EXPLAINS the missing link. */
  redistributable: boolean;
  /** `id` is OPTIONAL on purpose (`P-55`). It is what makes a workbook
   * downloadable, and a client meeting a server older than this release gets
   * `null` — which renders the title as plain text rather than as a link that
   * 404s. Absence has one unambiguous meaning here ("cannot build a download
   * URL"), which is why it is safe to default, unlike the version-skew cases
   * where both readings are wrong. */
  files: { id: string | null; filename: string; section: string | null }[];
}

function toProjectPriceList(dto: ProjectPriceListDto): ProjectPriceList {
  return {
    editionId: dto.edition_id,
    publisher: dto.publisher ?? null,
    region: dto.region ?? null,
    year: dto.year,
    title: dto.title,
    sourceUrl: dto.source_url ?? null,
    auto: dto.auto,
    matchedOn: dto.matched_on ?? null,
    redistributable: dto.redistributable ?? true,
    files: (dto.files ?? []).map((f) => ({
      id: f.id ?? null,
      filename: f.filename,
      section: f.section ?? null,
    })),
  };
}

/** Library price lists associated with a project (`P-44`). */
export function useProjectPriceLists(
  projectId: string | null,
): UseQueryResult<ProjectPriceList[], Error> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "project", projectId, "price-lists"],
    enabled: !!projectId,
    queryFn: async ({ signal }): Promise<ProjectPriceList[]> => {
      const res = await api.cmeinsight.get<{ items: ProjectPriceListDto[] }>(
        `/v1/projects/${projectId}/price-lists`,
        { signal },
      );
      return res.items.map(toProjectPriceList);
    },
  });
}

/** Wire shape of GET /v1/price-lists/editions (`P-47b`). */
export interface PriceListEditionDto {
  id: string;
  publisher?: string;
  region: string | null;
  redistributable?: boolean;
  year: number;
  title: string;
  source_url: string | null;
  file_count: number;
  associated: boolean;
  previously_removed: boolean;
}

/** One edition offered by the library picker (`P-47b`). */
export interface PriceListEdition {
  id: string;
  publisher: string | null;
  region: string | null;
  year: number;
  title: string;
  sourceUrl: string | null;
  /** See `ProjectPriceList.redistributable` (`P-58`). */
  redistributable: boolean;
  /**
   * How many workbooks this edition owns. Part of the contract, not decoration:
   * an edition with none stages nothing, so picking it would leave the project
   * exactly as blocked as before. The option is shown and disabled, with the
   * reason, rather than silently omitted.
   */
  fileCount: number;
  /** Already live on this project — offered but not choosable again. */
  associated: boolean;
  /** This project once held it and a human removed it. Still offered. */
  previouslyRemoved: boolean;
}

/** The library the picker draws from (`P-47b`). */
export interface PriceListLibrary {
  editions: PriceListEdition[];
  /**
   * The regions the library ACTUALLY holds, served by the API.
   *
   * Not a hard-coded string. The mockup promised «i prezzari di tutte le
   * regioni» over a three-region library, and a TypeScript copy of that list
   * goes false the day an edition is seeded — silently, because no test knows
   * what the database holds. Only real values ship.
   */
  regions: string[];
  /** The publishers the library ACTUALLY holds (`P-58`), same rule as
   * `regions`: served, never hard-coded. A commercial publisher contributes a
   * publisher and no region, so this is the only list that can describe the
   * whole library. */
  publishers: string[];
}

export function usePriceListLibrary(projectId: string | null): UseQueryResult<PriceListLibrary> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "price-list-library", projectId],
    enabled: !!projectId,
    queryFn: async ({ signal }): Promise<PriceListLibrary> => {
      const res = await api.cmeinsight.get<{
        items: PriceListEditionDto[];
        regions: string[];
        publishers?: string[];
      }>(`/v1/price-lists/editions?project_id=${projectId}`, { signal });
      return {
        editions: (res.items ?? []).map((d) => ({
          id: d.id,
          publisher: d.publisher ?? null,
          region: d.region ?? null,
          year: d.year,
          title: d.title,
          sourceUrl: d.source_url ?? null,
          redistributable: d.redistributable ?? true,
          fileCount: d.file_count,
          associated: !!d.associated,
          previouslyRemoved: !!d.previously_removed,
        })),
        regions: res.regions ?? [],
        publishers: res.publishers ?? [],
      };
    },
  });
}

/** Add a library price list to a project (`P-47b`).
 *
 * Both caches are invalidated, not just the associations: the library's own
 * rows carry `associated` / `previously_removed`, so a picker refreshed from a
 * stale library would go on offering an edition the project now holds. */
export function useAddProjectPriceList(): UseMutationResult<
  void,
  Error,
  { projectId: string; editionId: string }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, editionId }): Promise<void> =>
      api.cmeinsight.post<void>(`/v1/projects/${projectId}/price-lists`, {
        body: { edition_id: editionId },
      }),
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "price-lists"] });
      void qc.invalidateQueries({ queryKey: ["tender", "price-list-library", projectId] });
    },
  });
}

/** Remove a library price list from a project — the association, not the edition. */
export function useRemoveProjectPriceList(): UseMutationResult<
  void,
  Error,
  { projectId: string; editionId: string }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, editionId }): Promise<void> =>
      api.cmeinsight.del<void>(`/v1/projects/${projectId}/price-lists/${editionId}`),
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "price-lists"] });
      // `P-47b` — the LIBRARY cache too, for the reason its sibling above
      // already states and this one did not honour. The library's rows carry
      // `associated` / `previously_removed`, both derived from THIS table, so a
      // removal that refreshes only the associations leaves the picker showing
      // the just-freed edition greyed out as «già associato» — blocking the
      // remove-then-re-pick rescue this endpoint pairs with.
      //
      // Two writers of one derived value, and only one re-derived it. The
      // staleness clears on any unrelated refetch, so a walkthrough that
      // reloads between steps never sees it.
      void qc.invalidateQueries({ queryKey: ["tender", "price-list-library", projectId] });
    },
  });
}

export interface UploadDocumentInput {
  projectId: string;
  file: File;
  /** Pipeline role; the backend auto-detects from the filename if omitted. */
  role?: TenderDocRole;
  /**
   * Where the file sat inside the uploaded folder, e.g. `02.Documenti economici`.
   * Not cosmetic: the classifier uses it as a signal, and it is what keeps two
   * same-named files in different folders distinct.
   */
  folderPath?: string;
  /**
   * Observe the bytes leaving the machine (`P-41`). Passing this routes the
   * call onto the XHR transport in `api.ts`; omitting it keeps fetch.
   */
  onProgress?: (loaded: number, total: number) => void;
}

/** Upload a document to a project (multipart → POST /v1/projects/{id}/documents). */
export function useUploadDocument(): UseMutationResult<TenderDocument, Error, UploadDocumentInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      file,
      role,
      folderPath,
      onProgress,
    }: UploadDocumentInput): Promise<TenderDocument> => {
      const form = new FormData();
      form.append("file", file);
      if (role) form.append("role", role);
      if (folderPath) form.append("folder_path", folderPath);
      const dto = await api.cmeinsight.post<DocumentCreatedDto>(
        `/v1/projects/${projectId}/documents`,
        { formData: form, onUploadProgress: onProgress },
      );
      return createdToTenderDocument(dto);
    },
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "documents"] });
      void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
    },
  });
}

/**
 * Upload a ZIP of the whole tender (POST /v1/projects/{id}/documents/archive).
 *
 * The server expands it and imports each member with its folder intact, and
 * reports what it skipped. One request, not one per file — a portal download is
 * thirty files and thirty serial round trips is a minute of staring at a
 * spinner.
 */
/** Raised when the server accepted the ZIP and the import job then failed.
 *  Carries the job so a caller can name it, and `message` is the server's own
 *  Italian sentence — shown to the user verbatim. */
export class ArchiveImportFailed extends Error {
  constructor(readonly job: AnalysisJob) {
    super(job.error ?? "L'importazione dello ZIP non è riuscita.");
    this.name = "ArchiveImportFailed";
  }
}

export function useUploadArchive(): UseMutationResult<
  ArchiveImported,
  Error,
  {
    projectId: string;
    file: File;
    /** Observe the bytes leaving the machine (`P-41`) — see UploadDocumentInput. */
    onProgress?: (loaded: number, total: number) => void;
    /** Observe the SERVER importing them (`P-48`) — «12 di 30 documenti».
     *  Called with `null` once the ZIP is accepted but before the job has
     *  published a first count, so a caller can say "starting" rather than
     *  invent `0 di 0`. */
    onImportProgress?: (counts: { imported: number; total: number } | null) => void;
  }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      file,
      onProgress,
      onImportProgress,
    }): Promise<ArchiveImported> => {
      const form = new FormData();
      form.append("file", file);
      /* `P-48` step 2 — this POST now answers **202 with a job**, not 200 with
         the report. The server used to import inline, which measured 51,1 s of
         blocked browser on Omar's real 30-document bundle even after the import
         itself was made concurrent.

         This mutation still RESOLVES with the report, deliberately: everything
         downstream in `UploadView.submit()` — the empty-bundle notice, `P-45`'s
         partial count, the receipt, `uploadCommitted` — reads that shape, and
         re-deriving it at a second call site is how two paths that must agree
         stop agreeing. What changed underneath is that the work now survives the
         request: closing the tab no longer kills the import, and `JobsWatcher`
         announces its completion from anywhere in the app.

         WHAT IT DOES NOT DO, corrected in place because the sentence that stood
         here named `useLatestImportJob` -- a hook deleted in the same commit
         that wrote the claim. One grep, one hit: the comment itself. Reloading
         mid-import shows NOTHING on this page. `UploadView` holds `project` in
         plain `useState` and mounts at `/` with no project id in the URL, so a
         reload has nothing to reconstruct from; the backend endpoint that would
         back a fix (`/jobs/latest?kind=import`) exists and is correct, and no
         caller passes that kind. Open with Omar. */
      const started = toAnalysisJob(
        await api.cmeinsight.post<JobDto>(`/v1/projects/${projectId}/documents/archive`, {
          formData: form,
          onUploadProgress: onProgress,
        }),
      );
      onImportProgress?.(null);

      /* Poll to terminal, under a HARD deadline.
         The cadence is `importPollInterval`; the exits are `completed` and
         `failed`, and the server does write both -- a redeploy mid-import is
         turned into `failed` by the boot sweep in `main.py`.

         The deadline is not modelling how long an import takes. It is here
         because "the server always writes a terminal state" is a claim about
         code that can be switched off: the boot sweep has an opt-out env var,
         and it is wrapped in `try/except` so that a failure is logged and boot
         continues. Either of those leaves a row stuck at `running`, and without
         a bound this loop would poll it every 1,5 s for as long as the tab is
         open. Bounding it costs one comparison; modelling it would need
         throughput assumptions that are wrong the day the container changes. */
      let job = started;
      const deadline = Date.now() + IMPORT_POLL_DEADLINE_MS;
      let consecutiveFailures = 0;
      for (;;) {
        const wait = importPollInterval(job.status);
        if (wait === false) break;
        if (Date.now() > deadline) {
          throw new Error(
            "L'importazione dello ZIP non ha risposto entro il tempo previsto. " +
              "Ricarica la pagina: se i documenti ci sono, l'importazione è andata a buon fine.",
          );
        }
        await new Promise((r) => setTimeout(r, wait));
        /* A FAILED POLL IS NOT A FAILED IMPORT, and the first version of this
           loop could not tell them apart. A bare `await` here meant any
           rejection — a Wi-Fi blip, a 502 during a redeploy, the 30 s request
           timeout, an expired token refresh — left the mutation, and
           `submit()`'s catch renders that as « · non caricato» plus a button
           reading «Riprova i N file non caricati». All three are assertions that
           the bundle never reached the server. It did: the 202 is what started
           this loop, and the container keeps importing whether or not anyone is
           watching. Pressing that button re-POSTs the same ZIP — a 409 if the
           first import is still running, or a full DUPLICATE import if it has
           finished.
           The sibling never behaved this way: `useAnalysisJob` polls through
           `useQuery` with `retry: 1` and a `refetchInterval`, so a failed poll
           there is retried and the watch continues. This loop is hand-rolled
           because it must resolve with the report, so it has to say out loud
           what `useQuery` was doing for free. */
        try {
          job = toAnalysisJob(await api.cmeinsight.get<JobDto>(`/v1/jobs/${job.id}`));
          consecutiveFailures = 0;
        } catch (err) {
          consecutiveFailures += 1;
          if (consecutiveFailures > IMPORT_POLL_MAX_CONSECUTIVE_FAILURES) throw err;
          continue;
        }
        const counts = archiveImportCounts(job);
        if (counts) onImportProgress?.({ imported: counts.imported, total: counts.total });
      }

      if (job.status === "failed") throw new ArchiveImportFailed(job);

      const report = archiveReportFromJob(job);
      if (report === null) {
        /* Completed, but carrying no readable report. Throwing rather than
           returning an empty one is the whole point: an empty report is
           indistinguishable from "the ZIP contained nothing we could read",
           which `P-45` renders as a specific and alarming notice. Inventing
           that state from a missing field would be a confident lie about the
           user's bundle. */
        throw new Error(
          "L'importazione è terminata ma il server non ha restituito il riepilogo. " +
            "Ricarica la pagina per vedere i documenti importati.",
        );
      }
      return report;
    },
    onSuccess: (_data, { projectId }) => {
      /* Still correct HERE, and worth saying why, because `P-48` moved the
         moment this fires: the mutation now resolves when the JOB is complete,
         not when the POST returns, so the documents really are committed by the
         time these refetch. Had it resolved at the 202 this would have cached
         the empty pre-import answer. */
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "documents"] });
      void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
      /* `P-44` — the archive import is the moment the SERVER may auto-associate
         a price list, so the card's query must refetch here or it shows the
         pre-upload emptiness until a remount. Without this line the guarantee
         («the card lists what the analysis will use») has no consumer on the
         one path that creates it. */
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "price-lists"] });
    },
  });
}

/**
 * Everything a finished archive-import job carries, or `null` while it runs.
 *
 * The server writes the same report it used to RETURN into `Job.result.report`,
 * so a client reads one shape whether it watched the import happen or found it
 * already finished after a reload. Parsing lives here, beside the DTO types it
 * mirrors, rather than in the screen — the screen should not know the wire.
 *
 * Returns `null` rather than an empty report for an unfinished or unparseable
 * job, because "no documents imported" is a real and alarming state (`P-45`
 * shows a notice for it) and must never be manufactured by a missing field.
 */
export function archiveReportFromJob(job: AnalysisJob | null | undefined): ArchiveImported | null {
  if (!job || job.kind !== "import" || job.status !== "completed") return null;
  const report = job.result?.report as ArchiveImportedDto | undefined;
  if (!report || !Array.isArray(report.documents) || !Array.isArray(report.skipped)) return null;
  return {
    documents: report.documents.map(createdToTenderDocument),
    skipped: report.skipped,
    totalEntries: report.total_entries,
    lostFiles: typeof report.lost_files === "number" ? report.lost_files : null,
  };
}

/**
 * How far a running archive import has got, as the server counts it.
 *
 * `documents_imported` of `documents_total`, NOT of the ZIP's entry count: the
 * two real TORINO downloads declare 82 and 68 entries for 35 and 30 real
 * documents, so counting entries would stall a healthy import at 37% and then
 * jump. Both fields are written by the same statement on the server, so they
 * cannot disagree.
 *
 * `null` when the job has not published a count yet — which the caller must
 * render as "starting", never as `0 di 0`.
 */
export function archiveImportCounts(
  job: AnalysisJob | null | undefined,
): { imported: number; total: number; filename: string | null } | null {
  if (!job || job.kind !== "import") return null;
  /* Only while the job is actually RUNNING. Two independent reviews landed on
     the same defect here: the last progress snapshot is published when the final
     member finishes STAGING, which is before any row is written and before the
     transaction commits -- so a failed import's last `result` is a stale
     «N di N» that `_fail_import_job` never clears. Reading counts off a terminal
     job republished that snapshot on the very poll that precedes the failure,
     putting a full count on screen immediately before «non caricato». */
  if (job.status !== "queued" && job.status !== "running") return null;
  const total = job.result?.documents_total;
  const imported = job.result?.documents_imported;
  if (typeof total !== "number" || typeof imported !== "number") return null;
  const filename = job.result?.filename;
  return { imported, total, filename: typeof filename === "string" ? filename : null };
}

/**
 * Correct a document's role or kind (PATCH /v1/documents/{id}).
 *
 * `scope` serialises every document correction on the page, not just repeats on
 * one row: TanStack's scope id is static, so per-row scoping would need a
 * mutation instance per row. Serialising all of them is the conservative
 * reading and costs nothing at a bundle's thirty documents — two corrections
 * queue rather than racing, so the later response is always the last applied.
 *
 * Note this is *not* a double-submit guard: scope **queues** the second call
 * rather than dropping it. The caller still needs its own latch if repeat
 * clicks must not each produce a request.
 */
export function useUpdateDocument(): UseMutationResult<
  TenderDocument,
  Error,
  { id: string; projectId: string; role?: TenderDocRole; docKind?: string }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    scope: { id: "tender-document-update" },
    mutationFn: async ({ id, role, docKind }): Promise<TenderDocument> => {
      const dto = await api.cmeinsight.patch<DocumentSummaryDto>(`/v1/documents/${id}`, {
        body: { role, doc_kind: docKind },
      });
      return toTenderDocument(dto);
    },
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "documents"] });
      void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
    },
  });
}

/**
 * Short-lived presigned URL for a document's converted PDF (`INT-9`).
 *
 * A plain function, NOT a `useMutation` hook, and that is deliberate. The URL is
 * single-use and expires in minutes, so there is nothing for react-query to
 * cache or invalidate — and every `UploadView` harness in this suite renders
 * without a `QueryClientProvider`, keeping an explicit list of "hooks that need
 * a QueryClient" that has to be stubbed in each file. Adding a 51st entry to 50
 * files to buy caching we do not want is the wrong trade; needing no provider at
 * all is the right one.
 *
 * The filename and content type ride on the presigned URL itself, set by the
 * server: the object is stored as `.../original.pdf`, so without its
 * `Content-Disposition` every document in a bundle saves under the same name.
 */
export async function fetchDocumentDownloadUrl(api: Api, documentId: string): Promise<string> {
  const res = await api.cmeinsight.get<{ url: string }>(`/v1/documents/${documentId}/download`);
  return res.url;
}

/** A short-lived link to the exact price-list workbook this project prices
 * against (`P-55`).
 *
 * Same shape as `fetchDocumentDownloadUrl` and for the same reason — a plain
 * async function rather than a hook, so no test needs a QueryClient.
 *
 * The bytes behind this URL are the SAME R2 object the analysis staged, not a
 * copy: that is the whole point of the endpoint, and it is why the link must
 * never be built client-side from a filename.
 */
export async function fetchPriceListFileDownloadUrl(
  api: Api,
  projectId: string,
  fileId: string,
): Promise<string> {
  const res = await api.cmeinsight.get<{ url: string }>(
    `/v1/projects/${projectId}/price-lists/files/${fileId}/download`,
  );
  return res.url;
}

/** Delete a document (DELETE /v1/documents/{id} → status archived). */
export function useDeleteDocument(): UseMutationResult<
  void,
  Error,
  { id: string; projectId: string }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; projectId: string }): Promise<void> =>
      api.cmeinsight.del<void>(`/v1/documents/${id}`),
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "documents"] });
      void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
    },
  });
}

// ── Audit: categories / line-items / discrepancies ───────────────────────────
// The analyze job (Phase-A) populates these per project. Backend enums stay in
// English on the wire; the audit UI's helpers.ts applies the Italian labels.

/**
 * Kinds of finding the audit can carry.
 *
 * `price_unavailable` (`P-75`) and `total_mismatch` (`P-73`) are new, and
 * they are deliberately NOT variants of `price_mismatch`: one says the
 * comparison could not be made, the other says the tender document does not
 * add up against itself, and neither is "the contractor's price is wrong".
 *
 * `qty_mismatch` remains in the union and NOTHING CREATES ONE — it is in the
 * enum, in this type and in the Italian label «Quantità divergente», and no
 * code path has ever produced it, so the legend promises a check that does
 * not exist. Left rather than removed because removing it is a product
 * decision about the legend; recorded here so it is not mistaken for live.
 */
export type DiscrepancyType =
  | "price_mismatch"
  | "qty_mismatch"
  | "desc_mismatch"
  | "price_unavailable"
  | "total_mismatch";
export type Severity = "err" | "warn";
/** Backend has 4 states; the UI surfaces 3 (open+unresolved both read as "non risolto"). */
export type DiscrepancyStatus = "open" | "in_review" | "resolved" | "unresolved";

// --- categories ---
interface CategorySubDto {
  id: string;
  code: string | null;
  name: string;
  cost: number | null;
  eur_m2: number | null;
  pct: number | null;
  /** Goal 55B, L-1 (absent on an older server → 0). */
  unread_amounts?: number;
}
interface CategoryDto extends CategorySubDto {
  is_uncategorized: boolean;
  subs: CategorySubDto[];
}

export interface AuditCategorySub {
  id: string;
  code: string | null;
  name: string;
  /** Goal 55B, L-1: the sum of the amounts READ; null with `unreadAmounts` > 0 when every
   * amount behind it is unread. `pct` / `eurM2` are null when an amount behind them is. */
  cost: number | null;
  eurM2: number | null;
  pct: number | null;
  unreadAmounts?: number;
}
export interface AuditCategory extends AuditCategorySub {
  isUncategorized: boolean;
  subs: AuditCategorySub[];
}

function toCategorySub(dto: CategorySubDto): AuditCategorySub {
  return {
    id: dto.id,
    code: dto.code,
    name: dto.name,
    cost: dto.cost,
    eurM2: dto.eur_m2,
    pct: dto.pct,
    unreadAmounts: dto.unread_amounts ?? 0,
  };
}
export function toAuditCategory(dto: CategoryDto): AuditCategory {
  return {
    ...toCategorySub(dto),
    isUncategorized: dto.is_uncategorized,
    subs: dto.subs.map(toCategorySub),
  };
}

/**
 * Everything downstream of a category change. Category costs are summed live
 * from the line items, so moving one item moves the tree, the Pareto tab AND
 * the Strategia table — invalidating only "categories" would leave the money
 * screen showing the pre-move figure until the next reload.
 */
function invalidateProject(qc: ReturnType<typeof useQueryClient>, projectId: string): void {
  void qc.invalidateQueries({ queryKey: ["tender", "project", projectId] });
}

/** Live category tree for a project (GET /v1/projects/{id}/categories). */
export function useAuditCategories(projectId: string | null): UseQueryResult<AuditCategory[]> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "project", projectId, "categories"],
    enabled: !!projectId,
    queryFn: async ({ signal }): Promise<AuditCategory[]> => {
      const res = await api.cmeinsight.get<{ categories: CategoryDto[] }>(
        `/v1/projects/${projectId}/categories`,
        { signal },
      );
      return res.categories.map(toAuditCategory);
    },
  });
}

// --- line items + discrepancies ---
interface DiscrepancyBriefDto {
  id: string;
  type: DiscrepancyType;
  severity: Severity;
  status: DiscrepancyStatus;
  note: string;
  compare_value: string | null;
  compare_delta: string | null;
  compare_source: string | null;
}
interface LineItemDto {
  id: string;
  code: string | null;
  description: string;
  // Goal 55A, D19 (corrected 4 Oct): null = never read. An older server sends the stored
  // placeholder 0 instead — `toAuditLineItem` reads the item's record and drops it too.
  qty: number | null;
  unit: string | null;
  unit_price: number | null;
  total: number | null;
  pct_budget: number | null;
  category_id: string | null;
  suggested_category_id: string | null;
  discrepancy: DiscrepancyBriefDto | null;
  // Goal 55A — every flag (most severe first) and what Step 1 checked. Optional on the
  // wire: an older server sends neither, and the item is then «non controllata».
  discrepancies?: DiscrepancyBriefDto[];
  check_state?: CheckState | null;
  check_reason?: CheckReason | null;
  check_lists?: string | null;
  ref_price?: number | null;
  amount_read?: boolean | null;
  missing_fields?: string[];
}

export interface AuditDiscrepancyBrief {
  id: string;
  type: DiscrepancyType;
  severity: Severity;
  status: DiscrepancyStatus;
  note: string;
  compareValue: string | null;
  compareDelta: string | null;
  compareSource: string | null;
}
export interface AuditLineItem {
  id: string;
  code: string | null;
  description: string;
  /** Goal 55A, D19 (corrected 4 Oct): null = the bill's value was not read (absent,
   *  unreadable or ambiguous) — shown as not determinable, never as 0. */
  qty: number | null;
  unit: string | null;
  unitPrice: number | null;
  total: number | null;
  pctBudget: number | null;
  categoryId: string | null;
  suggestedCategoryId: string | null;
  /** The MAIN flag: the first of `discrepancies` (most severe first). */
  discrepancy: AuditDiscrepancyBrief | null;
  /** Goal 55A, D17: every flag of the item, most severe first, then type, then id.
   *  Optional so a caller holding an older shape still type-checks; read it through
   *  `itemFlags()`, which falls back to the main flag. */
  discrepancies?: AuditDiscrepancyBrief[];
  /** Goal 55A: what Step 1 checked for this item. Absent = never shown to be checked:
   *  read through `itemCheckState()`, which reports «non controllata». */
  checkState?: CheckState;
  checkReason?: CheckReason | null;
  checkLists?: string | null;
  refPrice?: number | null;
  amountRead?: boolean | null;
  missingFields?: string[];
}

/** Goal 55A, D17: every flag of an item (the main one alone for an older shape). */
export function itemFlags(item: AuditLineItem): AuditDiscrepancyBrief[] {
  return item.discrepancies ?? (item.discrepancy ? [item.discrepancy] : []);
}

/** Goal 55A: an item's check state as reported — absent is «non controllata», never checked. */
export function itemCheckState(item: AuditLineItem): {
  state: CheckState;
  reason: CheckReason | null;
} {
  if (!item.checkState) return { state: "non_controllata", reason: "esito_non_registrato" };
  return { state: item.checkState, reason: item.checkReason ?? null };
}

/** Goal 55A — one state per item (tender-api `analysis/checks.py`). */
export type CheckState =
  | "controllata_coerente"
  | "controllata_con_differenze"
  | "non_controllata"
  | "incompleta";
export type CheckReason =
  | "nessuna_riga_listino"
  | "prezzo_listino_zero"
  | "prezzo_listino_assente"
  | "prezzo_listino_ambiguo"
  | "nessun_listino"
  | "esito_non_registrato";

/** The fields an «incompleta» item reports, in the reader's words (`checks.FIELD_WORDS`). */
const FIELD_WORDS: Record<string, string> = {
  code: "codice",
  quantity: "quantità",
  unit_price: "prezzo unitario",
  amount: "importo",
};

/** Goal 55A, D19 (corrected 4 Oct): the fields whose value nobody read — named in the item's
 *  record («quantity», «quantity:ambiguo:12.345»), or an amount recorded as not read. */
export function unknownFields(
  missing: string[] | undefined,
  amountRead?: boolean | null,
): Set<string> {
  const out = new Set((missing ?? []).map((t) => t.split(":")[0]));
  if (amountRead === false) out.add("amount");
  return out;
}

/** The unread fields in the reader's words — the chatbot's own (`checks.unread_words`):
 *  «manca: codice, quantità», and «quantità: formato del numero ambiguo («12.345»)». */
export function unreadWords(missing: string[] | undefined): string {
  const plain: string[] = [];
  const ambiguous: string[] = [];
  for (const token of missing ?? []) {
    const [field, kind, ...rest] = token.split(":");
    const words = FIELD_WORDS[field] ?? field;
    if (kind === "ambiguo") {
      // Without its printed text when the saved record had to drop it (tender-api persist).
      const printed = rest.join(":");
      ambiguous.push(`${words}: formato del numero ambiguo${printed ? ` («${printed}»)` : ""}`);
    } else plain.push(words);
  }
  return [...(plain.length ? [`manca: ${plain.join(", ")}`] : []), ...ambiguous].join("; ");
}

function toDiscrepancyBrief(dto: DiscrepancyBriefDto): AuditDiscrepancyBrief {
  return {
    id: dto.id,
    type: dto.type,
    severity: dto.severity,
    status: dto.status,
    note: dto.note,
    compareValue: dto.compare_value,
    compareDelta: dto.compare_delta,
    compareSource: dto.compare_source,
  };
}
export function toAuditLineItem(dto: LineItemDto): AuditLineItem {
  // A value the item's record says was never read is unknown, whatever number stands in for
  // it on the wire (an older server sends the stored placeholder 0).
  const unknown = unknownFields(dto.missing_fields, dto.amount_read);
  return {
    id: dto.id,
    code: dto.code,
    description: dto.description,
    qty: unknown.has("quantity") ? null : dto.qty,
    unit: dto.unit,
    unitPrice: unknown.has("unit_price") ? null : dto.unit_price,
    total: unknown.has("amount") ? null : dto.total,
    pctBudget: dto.pct_budget,
    categoryId: dto.category_id,
    suggestedCategoryId: dto.suggested_category_id,
    discrepancy: dto.discrepancy ? toDiscrepancyBrief(dto.discrepancy) : null,
    discrepancies: (dto.discrepancies ?? (dto.discrepancy ? [dto.discrepancy] : [])).map(
      toDiscrepancyBrief,
    ),
    // A row the server says nothing about was never shown to have been checked.
    checkState: dto.check_state ?? "non_controllata",
    checkReason: dto.check_state ? (dto.check_reason ?? null) : "esito_non_registrato",
    checkLists: dto.check_lists ?? null,
    refPrice: dto.ref_price ?? null,
    amountRead: dto.amount_read ?? null,
    missingFields: dto.missing_fields ?? [],
  };
}

/** Goal 55A — per project: what Step 1 checked, over EVERY item (`GET …/check-summary`). */
export interface CheckBucket {
  count: number;
  eur: number;
  no_amount: number;
  pct: number | null;
  lists?: string[];
}
export interface AnalysisMarker {
  marker: "incompleta" | "controlli_discordanti" | "completezza_non_verificabile" | "completa";
  label?: string;
  reasons?: string[];
  checks: {
    total: {
      status: string | null;
      applicable: boolean;
      stated?: number | null;
      extracted?: number | null;
      residual?: number | null;
      residual_explained?: boolean | null;
      stated_page?: number | null;
      /** Goal 55A, last round: items whose amount was not read (`unread_amounts` status). */
      unread_amounts?: number | null;
    };
    categories: {
      status: string | null;
      applicable: boolean;
      axis?: string | null;
      /** Goal 55A, last round: categories holding an amount that was not read. */
      unread_categories?: number | null;
    };
  };
  failed_items: { id?: string; page?: number; reason?: string }[];
  failed_items_recorded?: boolean;
  failed_items_source?: { job_id?: string; match?: string } | null;
  incomplete_items: number;
}
export interface CheckSummary {
  items: number;
  known_eur: number;
  denominator_eur: number | null;
  states: Record<CheckState, CheckBucket>;
  reasons: Partial<Record<CheckReason, CheckBucket>>;
  flags: { open: number; resolved: number };
  marker: AnalysisMarker | null;
}

export function useCheckSummary(projectId: string | undefined | null) {
  const api = useApi();
  return useQuery<CheckSummary>({
    queryKey: ["tender", "project", projectId, "check-summary"],
    queryFn: () => api.cmeinsight.get<CheckSummary>(`/v1/projects/${projectId}/check-summary`),
    enabled: !!projectId,
  });
}

export interface LineItemFilters {
  hasDiscrepancy?: boolean;
  severity?: Severity;
  categoryId?: string;
  uncategorized?: boolean;
}

// --- Voci principali (Pareto) ---
// The fewest, largest items whose combined value reaches 80% of the project total
// (Omar, 2026-07-28 — supersedes the >=5%-per-item rule in the functional spec).
// `coveringCount` says how many of the returned items are genuinely part of that
// 80%; anything after that is context added to fill the list, and the UI must not
// present it as significant.

interface TopLineItemDto extends LineItemDto {
  cumulative_pct: number;
}
interface TopLineItemListDto {
  line_items: TopLineItemDto[];
  total: number;
  covering_count: number;
  coverage_pct: number;
  total_cme: number | null;
  /** Goal 55B, L-1 (absent on an older server → 0). */
  unread_amounts?: number;
}

export interface ParetoLineItem extends AuditLineItem {
  cumulativePct: number;
}
export interface TopLineItems {
  items: ParetoLineItem[];
  coveringCount: number;
  coveragePct: number;
  /** Goal 55B, L-1: the sum of the amounts READ (null when every amount is unread). */
  totalCme: number | null;
  /** Items left out of the ranking because nobody read their amount — the stated basis. */
  unreadAmounts?: number;
}

/** The Pareto view for a project (GET /v1/projects/{id}/top-line-items). */
export function useTopLineItems(projectId: string | null): UseQueryResult<TopLineItems> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "project", projectId, "top-line-items"],
    enabled: !!projectId,
    queryFn: async ({ signal }): Promise<TopLineItems> => {
      const res = await api.cmeinsight.get<TopLineItemListDto>(
        `/v1/projects/${projectId}/top-line-items`,
        { signal },
      );
      return {
        items: res.line_items.map((dto) => ({
          ...toAuditLineItem(dto),
          cumulativePct: dto.cumulative_pct,
        })),
        coveringCount: res.covering_count,
        coveragePct: res.coverage_pct,
        totalCme: res.total_cme,
        unreadAmounts: res.unread_amounts ?? 0,
      };
    },
  });
}

/** Live line items for a project, with optional filters (GET /v1/projects/{id}/line-items). */
export function useAuditLineItems(
  projectId: string | null,
  filters: LineItemFilters = {},
): UseQueryResult<AuditLineItem[]> {
  const api = useApi();
  const { hasDiscrepancy, severity, categoryId, uncategorized } = filters;
  return useQuery({
    queryKey: ["tender", "project", projectId, "line-items", filters],
    enabled: !!projectId,
    queryFn: async ({ signal }): Promise<AuditLineItem[]> => {
      const res = await api.cmeinsight.get<{ line_items: LineItemDto[]; total: number }>(
        `/v1/projects/${projectId}/line-items`,
        {
          signal,
          params: {
            has_discrepancy: hasDiscrepancy,
            severity,
            category_id: categoryId,
            uncategorized,
          },
        },
      );
      return res.line_items.map(toAuditLineItem);
    },
  });
}

// --- G3a: the bill page each item was read from ---

/** Physical PDF pages of the bill (1-based), as the layout scan recorded them. */
export interface LineItemPageSpan {
  pageStart: number;
  /** Equal to `pageStart` for a one-page item. */
  pageEnd: number;
}

interface LineItemPagesDto {
  items?: { line_item_id?: unknown; page_start?: unknown; page_end?: unknown }[];
}

function positivePage(v: unknown): number | null {
  return typeof v === "number" && Number.isInteger(v) && v > 0 ? v : null;
}

/** Parse `GET /v1/projects/{id}/line-item-pages`. An item absent from the answer — or one whose
 *  start page is not a page — has NO recorded page; the screen says «pagina non registrata». A
 *  payload with no `items` list is not an answer at all and is raised as an error, never read
 *  as «no item has a page». */
export function toLineItemPages(res: LineItemPagesDto): Map<string, LineItemPageSpan> {
  if (!res || !Array.isArray(res.items)) throw new Error("line-item-pages: no items list");
  const out = new Map<string, LineItemPageSpan>();
  for (const row of res.items) {
    if (!row || typeof row.line_item_id !== "string") continue;
    const start = positivePage(row.page_start);
    if (start == null) continue;
    const end = positivePage(row.page_end);
    out.set(row.line_item_id, {
      pageStart: start,
      pageEnd: end != null && end > start ? end : start,
    });
  }
  return out;
}

/** G3a — line item id → the bill pages it was read from (`GET …/line-item-pages`). Under the
 *  project's key prefix, so a new analysis (which invalidates the project) refreshes it. */
export function useLineItemPages(
  projectId: string | null,
): UseQueryResult<Map<string, LineItemPageSpan>> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "project", projectId, "line-item-pages"],
    enabled: !!projectId,
    queryFn: async ({ signal }) =>
      toLineItemPages(
        await api.cmeinsight.get<LineItemPagesDto>(`/v1/projects/${projectId}/line-item-pages`, {
          signal,
        }),
      ),
  });
}

/** The stored bill an item was read from, signed for an inline read (`GET …/bill-page`). */
export interface LineItemBillPage {
  url: string;
  pageStart: number | null;
  pageEnd: number | null;
  filename: string | null;
}

/** A fetcher, not a query: it is asked once per click, from inside the click (see
 *  `useBillPageOpener`), and a signed URL must not be served from a cache after it expires. */
export function useLineItemBillPage(): (lineItemId: string) => Promise<LineItemBillPage> {
  const api = useApi();
  return async (lineItemId: string) => {
    const dto = await api.cmeinsight.get<Record<string, unknown>>(
      `/v1/line-items/${lineItemId}/bill-page`,
    );
    if (!dto || typeof dto.url !== "string" || !dto.url) {
      throw new Error("bill-page: no url");
    }
    return {
      url: dto.url,
      pageStart: positivePage(dto.page_start),
      pageEnd: positivePage(dto.page_end),
      filename: typeof dto.filename === "string" ? dto.filename : null,
    };
  };
}

// --- discrepancy detail (with comments) ---
interface CommentDto {
  id: string;
  author_sub: string;
  text: string;
  created_at: string;
}
interface DiscrepancyDetailDto {
  id: string;
  project_id: string;
  line_item_id: string;
  type: DiscrepancyType;
  severity: Severity;
  status: DiscrepancyStatus;
  note: string;
  compare_value: string | null;
  compare_delta: string | null;
  compare_source: string | null;
  created_at: string;
  comments: CommentDto[];
}

export interface AuditComment {
  id: string;
  authorSub: string;
  text: string;
  createdAt: string;
}
export interface AuditDiscrepancyDetail extends AuditDiscrepancyBrief {
  projectId: string;
  lineItemId: string;
  createdAt: string;
  comments: AuditComment[];
}

function toComment(dto: CommentDto): AuditComment {
  return { id: dto.id, authorSub: dto.author_sub, text: dto.text, createdAt: dto.created_at };
}
export function toDiscrepancyDetail(dto: DiscrepancyDetailDto): AuditDiscrepancyDetail {
  return {
    ...toDiscrepancyBrief(dto),
    projectId: dto.project_id,
    lineItemId: dto.line_item_id,
    createdAt: dto.created_at,
    comments: dto.comments.map(toComment),
  };
}

/** One discrepancy with its comment thread (GET /v1/discrepancies/{id}). */
export function useDiscrepancy(
  discrepancyId: string | null,
): UseQueryResult<AuditDiscrepancyDetail> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "discrepancy", discrepancyId],
    enabled: !!discrepancyId,
    queryFn: async ({ signal }): Promise<AuditDiscrepancyDetail> => {
      const dto = await api.cmeinsight.get<DiscrepancyDetailDto>(
        `/v1/discrepancies/${discrepancyId}`,
        { signal },
      );
      return toDiscrepancyDetail(dto);
    },
  });
}

// --- audit mutations ---

/** Assign (or clear, with null) one line item's category (PATCH /v1/line-items/{id}). */
export function useCategorizeLineItem(
  projectId: string,
): UseMutationResult<AuditLineItem, Error, { lineItemId: string; categoryId: string | null }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ lineItemId, categoryId }): Promise<AuditLineItem> => {
      const dto = await api.cmeinsight.patch<LineItemDto>(`/v1/line-items/${lineItemId}`, {
        body: { category_id: categoryId },
      });
      return toAuditLineItem(dto);
    },
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Bulk-assign a category to many line items (PATCH /v1/line-items/bulk). */
export function useBulkCategorizeLineItems(
  projectId: string,
): UseMutationResult<{ updated: number }, Error, { ids: string[]; categoryId: string | null }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, categoryId }): Promise<{ updated: number }> =>
      api.cmeinsight.patch<{ updated: number }>(`/v1/line-items/bulk`, {
        body: { ids, category_id: categoryId },
      }),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

// --- manage categories (create / rename / re-parent / delete) ---

/**
 * Create a user-defined sub-category (POST /v1/projects/{id}/categories).
 * `parentId` is mandatory: main categories are the legal SOA taxonomy and are
 * never invented in the UI, so everything created here hangs off an existing one.
 */
export function useCreateCategory(
  projectId: string,
): UseMutationResult<
  AuditCategory,
  Error,
  { name: string; code: string | null; parentId: string }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, code, parentId }): Promise<AuditCategory> => {
      const dto = await api.cmeinsight.post<CategoryDto>(`/v1/projects/${projectId}/categories`, {
        body: { name, code, parent_id: parentId },
      });
      return toAuditCategory(dto);
    },
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Rename and/or move a category under a different main one (PATCH /v1/categories/{id}). */
export function useUpdateCategory(
  projectId: string,
): UseMutationResult<
  AuditCategory,
  Error,
  { categoryId: string; name?: string; code?: string | null; parentId?: string }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ categoryId, name, code, parentId }): Promise<AuditCategory> => {
      // Only send what actually changed: the API distinguishes "field omitted"
      // from "field set to null", and sending everything would blank a code the
      // user never touched.
      const body: Record<string, unknown> = {};
      if (name !== undefined) body.name = name;
      if (code !== undefined) body.code = code;
      if (parentId !== undefined) body.parent_id = parentId;
      const dto = await api.cmeinsight.patch<CategoryDto>(`/v1/categories/${categoryId}`, { body });
      return toAuditCategory(dto);
    },
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/**
 * Delete a category (DELETE /v1/categories/{id}). Its line items become
 * uncategorised; the response says how many. Rejected with 409 when the
 * category carries sub-categories, a make/buy strategy, an RFQ or a quote.
 */
export function useDeleteCategory(
  projectId: string,
): UseMutationResult<{ updated: number }, Error, { categoryId: string }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId }): Promise<{ updated: number }> =>
      api.cmeinsight.del<{ updated: number }>(`/v1/categories/${categoryId}`),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Update a discrepancy's status (PATCH /v1/discrepancies/{id}). */
export function useSetDiscrepancyStatus(
  projectId: string,
): UseMutationResult<
  AuditDiscrepancyDetail,
  Error,
  { discrepancyId: string; status: DiscrepancyStatus }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ discrepancyId, status }): Promise<AuditDiscrepancyDetail> => {
      const dto = await api.cmeinsight.patch<DiscrepancyDetailDto>(
        `/v1/discrepancies/${discrepancyId}`,
        { body: { status } },
      );
      return toDiscrepancyDetail(dto);
    },
    onSuccess: (_data, { discrepancyId }) => {
      void qc.invalidateQueries({ queryKey: ["tender", "discrepancy", discrepancyId] });
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "line-items"] });
      // Goal 55A: the open / resolved counts on the Audit screen move with a status change.
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "check-summary"] });
    },
  });
}

/** Add a comment to a discrepancy (POST /v1/discrepancies/{id}/comments). */
export function useAddDiscrepancyComment(): UseMutationResult<
  AuditComment,
  Error,
  { discrepancyId: string; text: string }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ discrepancyId, text }): Promise<AuditComment> => {
      const dto = await api.cmeinsight.post<CommentDto>(
        `/v1/discrepancies/${discrepancyId}/comments`,
        { body: { text } },
      );
      return toComment(dto);
    },
    onSuccess: (_data, { discrepancyId }) => {
      void qc.invalidateQueries({ queryKey: ["tender", "discrepancy", discrepancyId] });
    },
  });
}

// ── Analyze job ──────────────────────────────────────────────────────────────

/** Backend job lifecycle (api/models.py JobStatus). */
export type AnalysisJobStatus = "queued" | "running" | "completed" | "failed";

/** What a job is a job OF (models.py JobKind).
 *
 * `P-48` added the second kind. Until then every job was an analysis and the
 * client could omit the question entirely; now `/v1/jobs/active` returns both,
 * and a surface that does not ask will announce «Analisi completata» when a ZIP
 * finished importing.
 */
export type JobKind = "analyze" | "import";

/** Wire shape of JobCreated / JobStatusOut (api/schemas.py) — the poll superset.
 * Exported for the JobsWatcher's terminal-state fetch. */
export interface JobDto {
  id: string;
  project_id: string;
  /** Always sent — `kind` is non-optional on `JobStatusOut` and always has been.
   * Optional HERE only because the test doubles predate it; see `toAnalysisJob`. */
  kind?: string;
  status: string;
  progress?: number;
  error?: string | null;
  result?: Record<string, unknown> | null;
}

export interface AnalysisJob {
  id: string;
  projectId: string;
  kind: JobKind;
  status: AnalysisJobStatus;
  /** 0–100, as reported by the runner. */
  progress: number;
  error: string | null;
  result: Record<string, unknown> | null;
}

const KNOWN_JOB_STATUS = new Set<AnalysisJobStatus>(["queued", "running", "completed", "failed"]);

/** The quadratura verdict the backend writes onto `job.result.reconciliation`
 * (see `analysis/reconcile.py`). `over_extracted` means the analysis contains
 * MORE money than the source document declares, which is always a defect — the
 * TORINO Phase 2 shape, € 6.782.691,41 against a stated € 3.841.565,84. */
export interface ReconciliationVerdict {
  status:
    | "ok"
    | "over_extracted"
    | "under_extracted"
    | "no_stated_total"
    | "unreadable"
    // Goal 55A, last round (Omar, 4 Oct): some amounts were not read, so the read sum cannot
    // be compared with the document — «non verificabile», never «ok» and never a shortfall.
    | "unread_amounts";
  extracted: number;
  stated: number | null;
  stated_page: number | null;
  residual: number | null;
  residual_pct: number | null;
  explanation: string | null;
  /** Whether a shortfall was ACCOUNTED FOR by a line the document itself states.
   * Severity must key on this, never on `explanation !== null`: the backend
   * returns prose on both branches, including "this matches nothing, rows were
   * probably lost", so keying on presence rendered € 2.000.000 of dropped line
   * items as an amber "differenza spiegata". `null` = a verdict from before this
   * field existed; treated as the pre-existing behaviour, not as reassurance. */
  residual_explained?: boolean | null;
  /** Goal 55A, last round: how many items' amounts were not read. */
  unread_amounts?: number | null;
}

/** Read the verdict off an untyped job result, or null when absent (a job that
 * predates the check, or one that failed before it ran). A missing verdict is
 * NOT a pass — callers must not treat null as "fine". */
export function reconciliationOf(job: AnalysisJob): ReconciliationVerdict | null {
  const raw = job.result?.["reconciliation"];
  if (!raw || typeof raw !== "object") return null;
  const v = raw as Partial<ReconciliationVerdict>;
  return typeof v.status === "string" ? (v as ReconciliationVerdict) : null;
}

/** `P-47` — which tariffa codes two price books disagreed about, written onto
 * `job.result.price_book_overlap` (see `cme_processing.price_book_overlap`).
 *
 * `applicable: false` means this run had fewer than two price books, which is
 * every run until someone adds a second from the library picker. That is a
 * DIFFERENT statement from `null` below. */
export interface PriceBookOverlap {
  applicable: boolean;
  /** Basenames, in staging (precedence) order. `0_…` was uploaded for this
   * tender, `lib0_…` came from the library. */
  books: string[];
  /** How many codes appear in more than one book at DIFFERENT prices. A COUNT,
   * never capped — `examples` is the capped half. */
  codes: number;
  examples: {
    code: string;
    /** The price the customer was actually shown. */
    used: number;
    used_from: string;
    others: { price: number; from: string }[];
  }[];
  /** Every workbook the run STAGED, whether or not it could be read. */
  staged?: string[];
  /** Staged workbooks that priced NOTHING because the loader could not read
   * them. `books` is derived from rows, so an unreadable book is simply absent
   * from it — and a run that staged two and priced against one then reported
   * "fewer than two books, nothing to arbitrate". Named rather than inferred,
   * because "one book" and "one book survived" are different facts. */
  unreadable?: string[];
}

/** Read the overlap off an untyped job result, or null when this run wrote none.
 *
 * NULL IS A THIRD STATE and it is not "no overlap": a job that predates the
 * key, or a pipeline that died before the matcher. The two real answers both
 * arrive as an object with `applicable` true or false, so a caller never has to
 * guess which of the three it is holding. Same rule as `reconciliationOf`:
 * gate on the shape, never on emptiness. */
export function priceBookOverlapOf(job: AnalysisJob): PriceBookOverlap | null {
  const raw = job.result?.["price_book_overlap"];
  if (!raw || typeof raw !== "object") return null;
  const v = raw as Partial<PriceBookOverlap>;
  if (typeof v.applicable !== "boolean" || typeof v.codes !== "number") return null;
  return {
    applicable: v.applicable,
    books: Array.isArray(v.books) ? v.books : [],
    codes: v.codes,
    examples: Array.isArray(v.examples) ? v.examples : [],
    // Optional: a job written before these keys existed has neither, and an
    // empty array would claim "nothing was unreadable" about a run nobody
    // asked.
    staged: Array.isArray(v.staged) ? v.staged : undefined,
    unreadable: Array.isArray(v.unreadable) ? v.unreadable : undefined,
  };
}

/** `P-52` — which batches of the Elenco Prezzi a run failed to read. */
export interface ExtractionLoss {
  /** Zero is the GOOD answer and is reported explicitly. */
  batchesLost: number;
  /** `null` = the payload carried no total. `0` = the document yielded no
   * readable content at all, which is a LOSS, not a clean run. */
  batchesTotal: number | null;
  /** `span` is a range of CHUNKS, not PDF pages — the extractor re-cuts the
   * document into 4.000-character chunks and its log message inherited the
   * variable's name. `reason` is `empty_after_retries` or an exception class. */
  lost: { span: string; reason: string }[];
}

/** Read the extraction loss off an untyped job result, or null when absent.
 *
 * NULL IS A THIRD STATE and it is not "nothing was lost": a job that predates
 * the key, a pipeline that died before the Elenco Prezzi stage, or a resume
 * (which replays a preserved CSV and never re-reads the price list). The good
 * answer arrives as an object with `batches_lost: 0`. Gate on the SHAPE, never
 * on emptiness — same rule as `priceBookOverlapOf`, and the reason is the same:
 * a falsy check would fold "clean" and "unknown" into one silence, which is the
 * defect `P-52` exists to remove rather than relocate. */
export function extractionLossOf(job: AnalysisJob): ExtractionLoss | null {
  const raw = job.result?.["extraction_loss"];
  if (!raw || typeof raw !== "object") return null;
  const v = raw as Record<string, unknown>;
  if (typeof v["batches_lost"] !== "number") return null;
  const lost = Array.isArray(v["lost"]) ? v["lost"] : [];
  return {
    batchesLost: v["batches_lost"] as number,
    // NULL, not 0, and the difference cost two attempts to get right.
    // Defaulting an absent field to 0 made "the document yielded no readable
    // content" — a real state the writer emits — identical to "this payload
    // never carried the field". Requiring the field instead was WORSE: a
    // payload reporting five lost batches without a total would then return
    // null, and the user would be told nothing about a real loss. Three states,
    // three values.
    batchesTotal: typeof v["batches_total"] === "number" ? (v["batches_total"] as number) : null,
    lost: lost
      .filter((e): e is Record<string, unknown> => !!e && typeof e === "object")
      .map((e) => ({
        span: typeof e["span"] === "string" ? e["span"] : "",
        reason: typeof e["reason"] === "string" ? e["reason"] : "",
      })),
  };
}

/** G3a — the items account of a run (`job.result.items_account`): which rows the run merged
 * and dropped, and whether the items the scan found reconcile with the items saved. */
export interface ItemsAccountRow {
  code: string;
  /** Physical PDF pages of the bill, ascending, without repeats. */
  pages: number[];
  reason: string;
}
export interface ItemsAccount {
  merges: ItemsAccountRow[];
  drops: ItemsAccountRow[];
  /** Found ≠ saved: how many, and on which bill pages (ascending, without repeats). */
  unexplained: { count: number; pages: number[] };
  /** G3b — starts the run did not send as items (margin text, after the bill's last SOMMANO
   *  line, a page with no SOMMANO line), each with its page and the server's reason. Absent or
   *  empty for a run that predates G3b. */
  setAside?: ItemsAccountSetAside[];
  /** G3b — starts sent although their crop prints no SOMMANO line: how many, on which pages
   *  (ascending, without repeats). Absent for a run that predates G3b. */
  unclosedRead?: { count: number; pages: number[] };
}
export interface ItemsAccountSetAside {
  /** Physical PDF page of the start; empty when the server recorded none. */
  pages: number[];
  reason: string;
}

/** Pages from `page` (a number) or `pages` (a number or a list of numbers). */
function accountPages(...vals: unknown[]): number[] {
  const out = new Set<number>();
  for (const v of vals) {
    for (const p of Array.isArray(v) ? v : [v]) {
      if (typeof p === "number" && Number.isInteger(p) && p > 0) out.add(p);
    }
  }
  return [...out].sort((a, b) => a - b);
}

function accountRows(raw: unknown): ItemsAccountRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((e): e is Record<string, unknown> => !!e && typeof e === "object")
    .map((e) => ({
      code: typeof e["code"] === "string" ? e["code"] : "",
      pages: accountPages(e["pages"], e["page"]),
      reason: typeof e["reason"] === "string" ? e["reason"] : "",
    }));
}

/** Read the items account off an untyped job result, or null when there is none to read.
 *
 * NULL covers every analysis that cannot say: a job written before the account existed (no
 * key), an extraction that predates it (`{"status": "non_registrato"}`), and a payload of
 * another shape. Gated on the SHAPE, like `extractionLossOf`: an account must carry its
 * `unexplained.count` as a number, so a malformed one never reads as «nothing to report». */
export function itemsAccountOf(job: AnalysisJob): ItemsAccount | null {
  const raw = job.result?.["items_account"];
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const v = raw as Record<string, unknown>;
  const un = v["unexplained"];
  if (!un || typeof un !== "object") return null;
  const u = un as Record<string, unknown>;
  if (typeof u["count"] !== "number") return null;
  const listPages = (key: string, field: string): unknown[] =>
    Array.isArray(u[key])
      ? (u[key] as unknown[]).map((e) =>
          e && typeof e === "object" ? (e as Record<string, unknown>)[field] : null,
        )
      : [];
  return {
    merges: accountRows(v["merges"]),
    drops: accountRows(v["drops"]),
    unexplained: {
      count: u["count"] as number,
      pages: accountPages(
        ...listPages("lost", "pages"),
        ...listPages("duplicated", "pages"),
        ...listPages("unaccounted_starts", "page"),
      ),
    },
    setAside: accountEntries(v["set_aside"]).map((e) => ({
      pages: accountPages(e["page"]),
      reason: typeof e["reason"] === "string" ? e["reason"] : "",
    })),
    unclosedRead: (() => {
      const read = accountEntries(v["unclosed_read"]);
      return { count: read.length, pages: accountPages(...read.map((e) => e["page"])) };
    })(),
  };
}

/** The object entries of a G3b list key; anything else (absent, not a list, a non-object
 *  entry) contributes nothing, so a run without the key shows nothing new. */
function accountEntries(raw: unknown): Record<string, unknown>[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (e): e is Record<string, unknown> => !!e && typeof e === "object" && !Array.isArray(e),
  );
}

/** «p. 9» or «pp. 9, 14» — every page named, comma-separated: the spelling the server's
 *  marker reason uses for the same pages («Voci trovate e voci salvate non corrispondono: 1
 *  (pp. 1, 2)», `tender-api` G3a), so the panel and the marker never word one fact two ways. */
export function billPagesWords(pages: number[]): string {
  if (pages.length === 0) return "";
  return `${pages.length === 1 ? "p." : "pp."} ${pages.join(", ")}`;
}

/** One category as the document states it vs as we extracted it. */
export interface CategoryComparison {
  axis: string;
  code: string | null;
  name: string;
  stated: number;
  extracted: number | null;
  delta: number | null;
  /** `unread_amounts` (goal 55A, last round): the category holds an amount that was not read,
   * so it cannot be compared — never shown as a mismatch. */
  status: "ok" | "mismatch" | "unread_amounts";
}

/** The per-category verdict the backend writes onto
 * `job.result.category_reconciliation` (see `analysis/category_reconcile.py`).
 *
 * Distinct from the grand-total verdict above, and NOT redundant with it: on
 * TORINO the total reconciles to the cent while € 63.371,00 sits under the wrong
 * sub-category, because the two errors cancel in the sum. A `mismatch` here on
 * an `ok` there is the expected shape of misfiled — as opposed to lost — money.
 *
 * `not_comparable` and `axis_not_extracted` mean the check DECLINED to run, and
 * must never render as agreement. */
export interface CategoryReconciliationVerdict {
  status:
    | "ok"
    | "partial" // every compared category agreed, but a recap page was unreadable
    | "mismatch"
    | "axis_not_extracted"
    | "not_comparable" // read the document; it offers nothing comparable
    | "unreadable" // could not read the document at all — a different fact
    | "unread_amounts"; // goal 55A: no category disagrees, but some hold amounts not read
  axis: string | null;
  explanation: string | null;
  comparisons: CategoryComparison[];
  unparsed_recap_pages: number[];
  /** Recap pages recognised as NOT a category breakdown (a safety-cost summary),
   * set aside from the comparison. Reported so nothing vanishes; never a warning. */
  non_category_recap_pages: number[];
  /** Goal 55A, last round: categories holding an amount that was not read. */
  unread_categories?: number | null;
}

/** Read the per-category verdict off an untyped job result, or null when absent.
 * As with `reconciliationOf`, null is NOT a pass. */
export function categoryReconciliationOf(job: AnalysisJob): CategoryReconciliationVerdict | null {
  const raw = job.result?.["category_reconciliation"];
  if (!raw || typeof raw !== "object") return null;
  const v = raw as Partial<CategoryReconciliationVerdict>;
  if (typeof v.status !== "string") return null;
  return {
    status: v.status,
    axis: v.axis ?? null,
    explanation: v.explanation ?? null,
    // Defaulted rather than trusted: an older backend may omit either array,
    // and `.map` on undefined would blank the whole Audit page.
    // Coerced field by field, not just array-checked. Normalising the ARRAY but
    // trusting its MEMBERS is a half-guard: renaming `stated` in the backend's
    // `as_dict()` reached `fmtEurCents(undefined)` and threw inside render,
    // blanking the whole Audit route — the exact failure the "malformed verdict
    // does not blank the page" test exists to prevent, which only covered the
    // array being absent.
    comparisons: (Array.isArray(v.comparisons) ? v.comparisons : []).map((c) => {
      const raw = (c ?? {}) as Partial<CategoryComparison>;
      return {
        axis: typeof raw.axis === "string" ? raw.axis : "",
        code: typeof raw.code === "string" ? raw.code : null,
        name: typeof raw.name === "string" ? raw.name : "",
        stated: Number.isFinite(raw.stated) ? (raw.stated as number) : 0,
        extracted: Number.isFinite(raw.extracted) ? (raw.extracted as number) : null,
        delta: Number.isFinite(raw.delta) ? (raw.delta as number) : null,
        status:
          raw.status === "ok"
            ? "ok"
            : raw.status === "unread_amounts"
              ? "unread_amounts"
              : "mismatch",
      };
    }),
    unparsed_recap_pages: (Array.isArray(v.unparsed_recap_pages)
      ? v.unparsed_recap_pages
      : []
    ).filter((p): p is number => Number.isFinite(p)),
    non_category_recap_pages: (Array.isArray(v.non_category_recap_pages)
      ? v.non_category_recap_pages
      : []
    ).filter((p): p is number => Number.isFinite(p)),
    unread_categories: Number.isFinite(v.unread_categories)
      ? (v.unread_categories as number)
      : null,
  };
}

/**
 * Whether a **completion surface** may show what the reconciliation checks
 * concluded — `P-32`, decided by Omar on 2026-08-19.
 *
 * His words: *"the main assumption for this tool is to get the numbers right.
 * If we display issue the client will no longer believe the system... For the
 * time being keep the outcome but do not display it."*
 *
 * The decision is about WHERE the verdict is shown, not whether it is produced.
 * Nothing upstream changes: `analysis/reconcile.py` and
 * `analysis/category_reconcile.py` still run on every analysis and still write
 * `Job.result.reconciliation` / `Job.result.category_reconciliation`,
 * `reconciliationOf` and `categoryReconciliationOf` still read them, and
 * `ReconciliationBanner` + `CategoryReconciliationBanner` on the Audit page
 * still render them in full — figures, per-category breakdown, every tone —
 * for every role. What this flag silences is the two surfaces a customer meets
 * on the way past: the intake screen's completion card and the app-wide toast.
 *
 * So the signal survives exactly where a professional has opened the audit to
 * verify a number, and disappears where it only reads as the product doubting
 * itself.
 *
 * It is a flag rather than a deletion because Omar said *"for the time being"*,
 * and because the branch table behind it took four QA rounds to get right. Both
 * readers take it as a defaulted parameter (`completionDisplay`,
 * `completionToast`), so every test can drive **both** legs — which is the only
 * way an assertion that "no warning is shown" can be proven able to fail.
 */
export const SHOW_RECONCILIATION_ON_COMPLETION: boolean = false;

export function toAnalysisJob(dto: JobDto): AnalysisJob {
  const status = KNOWN_JOB_STATUS.has(dto.status as AnalysisJobStatus)
    ? (dto.status as AnalysisJobStatus)
    : "queued";
  return {
    id: dto.id,
    projectId: dto.project_id,
    /* Defaults to "analyze", and the default is safe for one specific reason
       rather than as a general shrug: `kind` is a REQUIRED field on
       `JobStatusOut`, so no deployed server can omit it, and the only producer
       of a `JobDto` without one is a test double written before `P-48`. For
       those, "analyze" is exactly what they meant. An unknown STRING is
       likewise read as an analysis rather than dropped — a job we cannot
       classify is still a job, and the alternative (silently ignoring it) is
       how a running job becomes invisible. */
    kind: dto.kind === "import" ? "import" : "analyze",
    status,
    progress: dto.progress ?? 0,
    error: dto.error ?? null,
    result: dto.result ?? null,
  };
}

/**
 * What `useStartAnalysis` takes: the project id alone — the ordinary start — or, after the
 * server refused over the user's work (`userWorkRefusalFromError`), the id together with the
 * confirmation token that refusal handed out.
 */
export type StartAnalysisVars = string | { projectId: string; confirmLoss: string };

/** Kick off the real Phase-A analysis (POST /v1/projects/{id}/analyze → 202 + job).
 * The backend dedups: if a run is already queued/running it returns THAT job. */
export function useStartAnalysis(): UseMutationResult<AnalysisJob, Error, StartAnalysisVars> {
  const api = useApi();
  return useMutation({
    mutationFn: async (vars: StartAnalysisVars): Promise<AnalysisJob> => {
      const projectId = typeof vars === "string" ? vars : vars.projectId;
      const path = `/v1/projects/${projectId}/analyze`;
      // Goal 55B, D16 — the confirmation travels only when the user gave it.
      const dto =
        typeof vars === "string"
          ? await api.cmeinsight.post<JobDto>(path)
          : await api.cmeinsight.post<JobDto>(path, { params: { confirm_loss: vars.confirmLoss } });
      return toAnalysisJob(dto);
    },
  });
}

/**
 * Goal 55B, D16 — the server refused to start because the run would delete the user's work
 * on the saved result: what would be lost (`lines`, worded by the server, one per type with
 * its count) and the token that confirms exactly that state.
 */
export interface UserWorkRefusal {
  message: string;
  lines: string[];
  confirmToken: string;
}

/** The 409 of `POST /v1/projects/{id}/analyze` over unconfirmed user work, or null. */
export function userWorkRefusalFromError(err: unknown): UserWorkRefusal | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const detail = (err.body as { detail?: unknown } | null)?.detail as
    | { message?: unknown; lines?: unknown; confirm_token?: unknown }
    | undefined;
  if (!detail || typeof detail.confirm_token !== "string" || !Array.isArray(detail.lines)) {
    return null;
  }
  return {
    message: typeof detail.message === "string" ? detail.message : "",
    lines: detail.lines.filter((l): l is string => typeof l === "string"),
    confirmToken: detail.confirm_token,
  };
}

/**
 * Poll cadence per job status: brisk while queued (startup is fast), relaxed
 * while running (the LLM phases take hours), stopped when terminal. Pure +
 * exported for unit tests.
 */
export function jobPollInterval(status: AnalysisJobStatus | undefined): number | false {
  if (status === "completed" || status === "failed") return false;
  if (status === "running") return 15_000;
  return 2_500;
}

/**
 * If `err` is the analyze pre-flight 422, return the missing document roles
 * (the UI maps them to Italian labels via DOC_ROLE_LABEL); null otherwise.
 */
export function missingRolesFromError(err: unknown): TenderDocRole[] | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null;
  const detail = (err.body as { detail?: { missing_roles?: unknown } } | null)?.detail;
  const roles = detail?.missing_roles;
  if (!Array.isArray(roles)) return null;
  return roles.filter((r): r is TenderDocRole => typeof r === "string");
}

/**
 * Poll an analysis job (GET /v1/jobs/{id}) until it reaches a terminal state.
 * On completion the tender caches are invalidated so the header / audit tree /
 * dashboard pick up the freshly-persisted line items.
 */
export function useAnalysisJob(jobId: string | null): UseQueryResult<AnalysisJob> {
  const api = useApi();
  const qc = useQueryClient();
  return useQuery<AnalysisJob>({
    queryKey: ["tender", "job", jobId],
    enabled: !!jobId,
    refetchInterval: (query) => jobPollInterval(query.state.data?.status),
    queryFn: async ({ signal }): Promise<AnalysisJob> => {
      const job = toAnalysisJob(await api.cmeinsight.get<JobDto>(`/v1/jobs/${jobId}`, { signal }));
      if (job.status === "completed") {
        // New categories / line items / totals exist — refresh the domain.
        void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
        void qc.invalidateQueries({ queryKey: ["tender", "project", job.projectId] });
      }
      return job;
    },
  });
}

/**
 * Most recent ANALYZE job for a project (GET /v1/projects/{id}/jobs/latest,
 * whose `kind` defaults to analyze, which is what this hook relies on) —
 * lets a reloaded/reconnected client rediscover an in-flight or finished run.
 * Resolves to null when the project has never been analyzed (backend 404).
 * While the rediscovered job is active it keeps polling on the same cadence,
 * and invalidates the tender caches when it completes.
 */
export function useLatestAnalysisJob(projectId: string | null): UseQueryResult<AnalysisJob | null> {
  const api = useApi();
  const qc = useQueryClient();
  return useQuery<AnalysisJob | null>({
    queryKey: ["tender", "project", projectId, "latest-job"],
    enabled: !!projectId,
    refetchInterval: (query) => {
      const job = query.state.data;
      return job ? jobPollInterval(job.status) : false;
    },
    queryFn: async ({ signal }): Promise<AnalysisJob | null> => {
      try {
        const dto = await api.cmeinsight.get<JobDto>(`/v1/projects/${projectId}/jobs/latest`, {
          signal,
        });
        const job = toAnalysisJob(dto);
        if (job.status === "completed") {
          void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
          /**
           * The predicate is NOT decoration — without it this query invalidates
           * ITSELF, for ever.
           *
           * `invalidateQueries` matches by PREFIX, and this hook's own key is
           * `["tender", "project", id, "latest-job"]` — which
           * `["tender", "project", id]` matches. An active query that is
           * invalidated refetches; the refetch observes `completed` again and
           * invalidates again. Nothing bounds it.
           *
           * It does not fire on mount, which is why it hid: the invalidation
           * raised DURING the first fetch is absorbed by that fetch. It needs
           * one external kick AFTER the query has settled — and the app has
           * several. `invalidateProject` (:1306) fires on every category move,
           * and `JobsWatcher` invalidates the whole `["tender"]` prefix when any
           * job completes, on every screen.
           *
           * Measured, one variable, with a control:
           *
           *   status = completed  ->  1 fetch settled, then ONE kick -> 301 (capped)
           *   status = running    ->  1 fetch settled, then ONE kick ->   2
           *
           * PRE-EXISTING and live on QA: this block is byte-identical at `HEAD`,
           * and `AuditPage:85` has mounted this hook all along. `P-63` did not
           * cause it — it added a second door, because the preparation route
           * mounts `useAnalysisJob` too and its own completion invalidation is
           * exactly such a kick.
           *
           * Excluding this key is semantically right, not a workaround: the
           * point of the invalidation is that new line items, categories and
           * totals now exist downstream. This query is not downstream of that —
           * it is the thing REPORTING it, and it has just fetched.
           */
          void qc.invalidateQueries({
            queryKey: ["tender", "project", job.projectId],
            predicate: (q) => q.queryKey[3] !== "latest-job",
          });
        }
        return job;
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null; // never analyzed
        throw e;
      }
    },
  });
}

/**
 * Poll cadence for an ARCHIVE IMPORT, which is a different animal.
 *
 * `jobPollInterval` relaxes to 15 s once a job is running, which is right for an
 * analysis measured in hours. A real 30-document import measures **51,1 s**, so
 * the same cadence would move the bar about three times and then finish — a
 * progress display that updates three times is the frozen screen `P-48` exists
 * to remove, wearing a costume. 1,5 s gives ~34 updates across that import and
 * costs one cheap indexed read per tick, for at most a minute.
 */
/**
 * How long the client will wait for an import before giving up on WATCHING it.
 *
 * Generous on purpose, and deliberately NOT derived from throughput: a real
 * 67 MB / 30-document bundle imports in 51,1 s, the archive cap is 512 MB, and
 * any figure computed from those two would be a model of a container that can
 * be resized tomorrow. This is a backstop against a job row that never reaches
 * a terminal state, not an estimate of a slow one.
 *
 * Reaching it does NOT mean the import failed — the copy says so, because the
 * server may well have finished after the client stopped looking.
 */
export const IMPORT_POLL_DEADLINE_MS = 30 * 60 * 1000;

/**
 * How many polls in a row may fail before the client gives up WATCHING.
 *
 * Consecutive, not cumulative: a blip in the middle of a 34-poll import must
 * cost nothing, while a backend that has genuinely gone away should not be
 * polled for the full thirty minutes. Six at the 1,5 s cadence is ~9 s of
 * uninterrupted failure — longer than a redeploy's connection gap, shorter than
 * a person's patience.
 *
 * Reaching it re-throws the LAST transport error rather than inventing a
 * sentence, so the user sees why it stopped.
 */
export const IMPORT_POLL_MAX_CONSECUTIVE_FAILURES = 6;

export function importPollInterval(status: AnalysisJobStatus | undefined): number | false {
  if (status === "completed" || status === "failed") return false;
  return 1_500;
}

/**
 * The archive import currently running for this ORG, or null (`P-48` round 4).
 *
 * Org-wide on purpose, and that is what makes it work after a reload. The
 * upload screen mounts at `/` with no project id in the URL and holds `project`
 * in plain `useState`, so a reload has nothing to reconstruct a project-scoped
 * query from. `/v1/jobs/active` needs no id: it answers "is one running?" for
 * the whole organisation, which is exactly the question a freshly-mounted page
 * has to ask.
 *
 * The gap this closes was found by an independent tester and is a data hazard,
 * not a missing nicety: a reload mid-import left a blank «Nuovo progetto»
 * screen, and a user who reasonably concluded the upload had not taken and
 * dropped the same ZIP again got a SECOND project importing a second copy of
 * the same tender. The 409 could not fire — the two uploads target different,
 * freshly-minted project ids, so the per-project guard never sees them.
 *
 * Polls on the IMPORT cadence while one is running (an import is ~1 minute, so
 * the 15 s feed used by the completion watcher would show three updates), and
 * falls back to that slower feed's cadence when nothing is running, because
 * then it is only waiting to notice a start.
 */
export function useActiveImportJob(): UseQueryResult<AnalysisJob | null> {
  const api = useApi();
  return useQuery<AnalysisJob | null>({
    queryKey: ["tender", "active-import"],
    refetchInterval: (query) => (query.state.data ? 1_500 : 15_000),
    queryFn: async ({ signal }): Promise<AnalysisJob | null> => {
      const dto = await api.cmeinsight.get<JobDto[]>("/v1/jobs/active", { signal });
      const jobs = Array.isArray(dto) ? dto.map(toAnalysisJob) : [];
      /* Oldest first, so a second import starting while one runs cannot make
         the banner jump between them. There should never BE two — the partial
         unique index refuses a second per project — but two projects can each
         have one, and the screen must pick deterministically. */
      const imports = jobs
        .filter((j) => j.kind === "import")
        .sort((a, b) => a.id.localeCompare(b.id));
      return imports[0] ?? null;
    },
  });
}

/**
 * The org's queued/running jobs of EVERY kind (GET /v1/jobs/active) — the feed
 * for the app-wide completion watcher. Light steady poll while the app is open.
 *
 * `P-48` widened the endpoint from `kind = 'analyze'`; this sentence said
 * «analyze jobs» for a release afterwards, which is the claim the change exists
 * to falsify. Every consumer must read `kind`: an import and an analysis are
 * different events with different copy and different destinations.
 */
export function useActiveJobs(): UseQueryResult<AnalysisJob[]> {
  const api = useApi();
  return useQuery<AnalysisJob[]>({
    queryKey: ["tender", "jobs", "active"],
    refetchInterval: 15_000,
    queryFn: async ({ signal }): Promise<AnalysisJob[]> => {
      const rows = await api.cmeinsight.get<JobDto[]>("/v1/jobs/active", { signal });
      return rows.map(toAnalysisJob);
    },
  });
}

// ── Assistente CME (project assistant) ───────────────────────────────────────

/** Wire shape of AssistantContextStats (api/schemas.py). */
interface AssistantContextDto {
  categories: number;
  line_items: number;
  discrepancies: number;
  uncategorized: number;
  categories_shown: number;
  top_items_shown: number;
  discrepancies_shown: number;
  /** Goal 55B, L-1: the sum of the amounts READ; null when every amount is unread. */
  total_cme: number | null;
  kb_status: KbStatus;
  kb_documents: number;
  kb_syntheses: number;
  kb_project_sources: number;
  kb_company_sources: number;
  /** Phase 2: recognised facts in the pack. Absent on an older server → 0. */
  kb_claims?: number;
}

/** Wire shape of AssistantSource (api/schemas.py) — `KBR-74`. */
interface AssistantSourceDto {
  kind: string;
  title: string;
  scope: string;
  label: string;
  document_id: string | null;
  page_id: string | null;
  page_start: number | null;
  page_end: number | null;
  /** `FE-2`. The `[E#]` markers the server resolved onto THIS document/page.
   *  Absent on an older server → `[]`, which reads as «no marker», not as a
   *  guess. Several markers share one entry when text and image evidence come
   *  from the same PDF sheet — that many-to-one is the point, not a collision. */
  evidence_ids?: string[];
}

interface AssistantAnswerDto {
  answer: string;
  context: AssistantContextDto;
  /** `FE-3`. `markers` when the answer's own `[E#]` references were resolved,
   *  `pages` when it wrote none and the list below is simply everything read.
   *  Absent on an older server → treated as `pages`, the weaker of the two. */
  citation_mode?: string;
  /** Absent on an older server → no clickable sources. The counts in `context`
   *  remain the statement about evidence; this is only about openability. */
  sources?: AssistantSourceDto[];
}

/**
 * What happened to the KNOWLEDGE half of an answer (`P-68`).
 *
 * `empty` and `unavailable` produce the same zero counts and must never produce
 * the same sentence on screen: the first means this project's documents were
 * searched and hold nothing relevant, the second means they were not searched at
 * all. Collapsing them is how a half-informed answer reads as a complete one.
 *
 *  - `off`         — this environment has no Knowledge Base wired in
 *  - `ok`          — passages were retrieved and the answer used them
 *  - `empty`       — searched; nothing relevant in this project or company knowledge
 *  - `unavailable` — the Knowledge Base could not be reached
 *  - `no_access`   — the user has not granted this app access to their KB
 */
/** `partial` is `FE-5`: half or more of the lookups failed AND at least one page
 *  was still read. It used to render as `unavailable`, whose sentence says no
 *  project document contributed — beside the chips for the ones that did. */
export type KbStatus = "off" | "ok" | "empty" | "partial" | "unavailable" | "no_access";

/** What the answer was actually based on — rendered under the reply so a capped
 *  list is never mistaken for a complete one. */
export interface AssistantContext {
  categories: number;
  lineItems: number;
  discrepancies: number;
  uncategorized: number;
  categoriesShown: number;
  topItemsShown: number;
  discrepanciesShown: number;
  totalCme: number | null;
  kbStatus: KbStatus;
  kbDocuments: number;
  kbSyntheses: number;
  kbProjectSources: number;
  kbCompanySources: number;
  kbClaims: number;
}

/**
 * One piece of evidence the answer stood on, as something the reader can OPEN
 * (`KBR-74`).
 *
 * Exactly one of `documentId` / `pageId` is set — a passage from the customer's
 * own PDF has a document, a synthesized page has a page. The browser resolves
 * neither: it hands the id back to the Knowledge Base with the reader's own
 * token, which applies its own access rule. Holding an id grants nothing.
 */
export interface AssistantSource {
  kind: "document" | "synthesis";
  title: string;
  scope: "project" | "company";
  label: string;
  documentId: string | null;
  pageId: string | null;
  pageStart: number | null;
  pageEnd: number | null;
  /** The `[E#]` markers this chip answers for. Empty when the answer wrote
   *  none — see `citationMode`. */
  evidenceIds: string[];
}

export interface AssistantReply {
  answer: string;
  context: AssistantContext;
  sources: AssistantSource[];
  /** `FE-3`. «riferimenti risolti» vs «pagine consultate». A resolved reference
   *  still does not prove the page SUPPORTS the sentence — the screen says that
   *  too, because neither the server nor this client evaluates entailment. */
  citationMode: "markers" | "pages";
}

/** A prior turn replayed to the backend so follow-up questions make sense. */
export interface AssistantTurn {
  role: "user" | "assistant";
  content: string;
}

/** Wire shapes of a watched run (29 Sep build round). */
interface AssistantStepPageDto {
  documentId: string | null;
  documentTitle: string | null;
  page: number | null;
  fromOcr: boolean;
}

interface AssistantStepDto {
  index: number;
  tool: string;
  summary: string;
  isError: boolean;
  pages?: AssistantStepPageDto[];
}

interface AssistantRunCreatedDto {
  runId: string;
  status: string;
  model: string;
  pollAfterMs?: number;
}

interface AssistantRunStatusDto {
  runId: string;
  status: string;
  model: string;
  elapsedS: number;
  steps?: AssistantStepDto[];
  answer: AssistantAnswerDto | null;
}

/** One lookup the assistant made, as the rail shows it while the answer is built. */
export interface AssistantStepView {
  index: number;
  tool: string;
  summary: string;
  isError: boolean;
  pages: { documentTitle: string; page: number | null; fromOcr: boolean }[];
}

/** What the browser knows about an answer that is still being built. */
export interface AssistantProgress {
  status: string;
  elapsedS: number;
  steps: AssistantStepView[];
}

/** How often the browser asks how the answer is going. */
export const ASSISTANT_POLL_INTERVAL_MS = 1_500;

/**
 * How long the browser keeps watching. ABOVE everything the server may still
 * legitimately take, so the browser never gives up on an answer the server is
 * allowed to deliver — the defect `P-89` recorded when the client deadline sat
 * below the server's. That is the five-minute answer (`RUN_DEADLINE_S` 300 s)
 * plus the bookkeeping after it (`RUN_POST_LOOP_RESERVE_S` 10 s) plus the time
 * a run whose process stopped takes to be REPORTED as interrupted
 * (`STALE_AFTER_S` 120 s): 430 s, rounded up to 450 s so a final poll lands
 * after it. The first value, 390 s, covered the answer but not the
 * interrupted report, so a reader could be told «no answer» 40 s before the
 * server would have said what it had found (`L1`, independent backend review of
 * 29 Sep).
 */
export const ASSISTANT_WATCH_DEADLINE_MS = 450_000;

/**
 * A failed poll is not a failed answer: the run keeps working on the server
 * whether or not a poll gets through. Six in a row (~9 s) covers a redeploy's
 * connection gap, the same bound the archive import uses.
 */
export const ASSISTANT_POLL_MAX_CONSECUTIVE_FAILURES = 6;

function toStepView(step: AssistantStepDto): AssistantStepView {
  return {
    index: step.index,
    tool: step.tool,
    summary: step.summary,
    isError: step.isError,
    pages: (step.pages ?? []).map((p) => ({
      documentTitle: p.documentTitle ?? "",
      page: p.page ?? null,
      fromOcr: Boolean(p.fromOcr),
    })),
  };
}

/**
 * Ask the project assistant, and wait for the answer while it is built.
 *
 * **29 Sep build round.** The answer used to arrive in the response to ONE
 * request — and the hosting platform cuts every request at ~60 s, so a
 * question needing longer was answered from half its evidence or not at all
 * (Gate B: 17 of 60). Now the question STARTS a run
 * (`POST /v1/projects/{id}/assistant/runs`) and this function polls it
 * (`GET …/runs/{runId}`) until it ends; each request is a short one, so the
 * cut never applies, and an answer may work for up to five minutes. Every poll
 * reports the steps so far to `onProgress`, which is what the rail shows while
 * the reader waits.
 *
 * The name and the first four arguments are unchanged on purpose: nine test
 * files mock this function by name, and the thread store calls it the same way.
 *
 * A run that ends `failed` or `interrupted` still carries an answer — a
 * declared partial that says what was found and what is missing — and that
 * answer is delivered. Only a run that ended with NO answer is an error.
 */
export async function askProjectAssistant(
  api: Api,
  projectId: string,
  question: string,
  history: AssistantTurn[] = [],
  onProgress?: (progress: AssistantProgress) => void,
): Promise<AssistantReply> {
  // `P-68` — the signed-in user's own Knowledge Base token, relayed so
  // tender-api can read THIS customer's documents as this customer. `null` when
  // one cannot be minted silently; the header is then simply absent and the
  // backend answers from the Computo alone and says so. Never awaited in a way
  // that can reject: `api.kbToken` swallows its own failures for this reason.
  const kbToken = await api.kbToken();
  const started = await api.cmeinsight.post<AssistantRunCreatedDto>(
    `/v1/projects/${projectId}/assistant/runs`,
    {
      body: { question, history },
      headers: kbToken ? { "X-CME-KB-Authorization": `Bearer ${kbToken}` } : {},
    },
  );
  const interval = started.pollAfterMs ?? ASSISTANT_POLL_INTERVAL_MS;
  const deadline = Date.now() + ASSISTANT_WATCH_DEADLINE_MS;
  let consecutiveFailures = 0;
  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, interval));
    if (Date.now() > deadline) {
      throw new Error("La risposta non è arrivata entro il tempo previsto. Riprova la domanda.");
    }
    let run: AssistantRunStatusDto;
    try {
      run = await api.cmeinsight.get<AssistantRunStatusDto>(
        `/v1/projects/${projectId}/assistant/runs/${started.runId}`,
      );
      consecutiveFailures = 0;
    } catch (err) {
      consecutiveFailures += 1;
      if (consecutiveFailures > ASSISTANT_POLL_MAX_CONSECUTIVE_FAILURES) throw err;
      continue;
    }
    onProgress?.({
      status: run.status,
      elapsedS: run.elapsedS,
      steps: (run.steps ?? []).map(toStepView),
    });
    if (run.status === "running") continue;
    if (run.answer) return toAssistantReply(run.answer);
    throw new Error("La risposta non è stata prodotta.");
  }
}

/** The server's answer in the shape the rail renders. Unchanged by the build
 *  round: only how it is DELIVERED changed. */
export function toAssistantReply(dto: AssistantAnswerDto): AssistantReply {
  return {
    answer: dto.answer,
    // A source carrying neither id cannot be opened, so it is not offered —
    // the same rule the server applies, restated here because an older server
    // is exactly the case that produces one.
    sources: (dto.sources ?? [])
      .filter((s) => typeof s.document_id === "string" || typeof s.page_id === "string")
      .map((s) => ({
        kind: s.kind === "synthesis" ? ("synthesis" as const) : ("document" as const),
        title: s.title,
        scope: s.scope === "project" ? ("project" as const) : ("company" as const),
        label: s.label,
        documentId: s.document_id ?? null,
        pageId: s.page_id ?? null,
        pageStart: s.page_start ?? null,
        pageEnd: s.page_end ?? null,
        evidenceIds: Array.isArray(s.evidence_ids) ? s.evidence_ids.map(String) : [],
      })),
    citationMode: dto.citation_mode === "markers" ? ("markers" as const) : ("pages" as const),
    context: {
      categories: dto.context.categories,
      lineItems: dto.context.line_items,
      discrepancies: dto.context.discrepancies,
      uncategorized: dto.context.uncategorized,
      categoriesShown: dto.context.categories_shown,
      topItemsShown: dto.context.top_items_shown,
      discrepanciesShown: dto.context.discrepancies_shown,
      totalCme: dto.context.total_cme,
      // Defaulted rather than asserted: a browser tab open across a deploy will
      // read a reply from the previous build, which carries none of these. The
      // honest fallback is "no knowledge half", not `undefined` reaching the
      // footer as `NaN documenti`.
      kbStatus: dto.context.kb_status ?? "off",
      kbDocuments: dto.context.kb_documents ?? 0,
      kbSyntheses: dto.context.kb_syntheses ?? 0,
      kbProjectSources: dto.context.kb_project_sources ?? 0,
      kbCompanySources: dto.context.kb_company_sources ?? 0,
      kbClaims: dto.context.kb_claims ?? 0,
    },
  };
}

// ── KB-11: the Knowledge Base approval gate ────────────────────────────────

export interface KbForwardDecisionInput {
  projectId: string;
  /** Documents the Admin wants synthesised. */
  approve: string[];
  /** Documents the Admin explicitly does not want. */
  skip: string[];
}

/** Gate B, 2026-09-28 — a document the server would not send, and why. */
export interface KbForwardRefusal {
  id: string;
  filename: string;
  reason: string;
}

export interface KbForwardReport {
  queued: string[];
  skipped: string[];
  /**
   * Ids the server did not move: already forwarded, already decided, or no
   * longer eligible. Returned rather than raised, so one stale id in a list of
   * eighteen does not lose the other seventeen — surface it, don't swallow it.
   */
  ignored: string[];
  /**
   * Gate B — asked for, eligible, and refused BEFORE being queued (today: too
   * large for the forwarder to carry), each with the server's sentence. Never
   * also in `queued`, so a receipt built from `queued` cannot call them sent.
   */
  refused: KbForwardRefusal[];
}

/**
 * Decide which of a project's documents reach the Knowledge Base.
 *
 * **This is where money is spent.** A tender bundle proposes ~18 documents and
 * ~1.6M input tokens of synthesis, and nothing leaves for the KB until this call
 * says so (Omar, 2026-08-12). Admin-only server-side; the screen hides the
 * control for everyone else so a member is never shown a button that 403s.
 *
 * `queued` is a promise, not a result: forwarding runs after the response and
 * each document's outcome lands on its own row, which is why the caller
 * invalidates the document list rather than patching state from this reply.
 */
export function useDecideKbForward(): UseMutationResult<
  KbForwardReport,
  Error,
  KbForwardDecisionInput
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, approve, skip }): Promise<KbForwardReport> => {
      const dto = await api.cmeinsight.post<{
        queued: string[];
        skipped: string[];
        ignored: string[];
        refused?: KbForwardRefusal[];
      }>(`/v1/projects/${projectId}/documents/kb-forward`, { body: { approve, skip } });
      return {
        queued: dto.queued,
        skipped: dto.skipped,
        ignored: dto.ignored,
        // `?? []`: a server that predates the field refuses nothing at the gate.
        refused: (dto.refused ?? []).map((r) => ({
          id: r.id,
          filename: r.filename,
          reason: r.reason,
        })),
      };
    },
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "documents"] });
    },
  });
}
